<template>
  <NavBar pageName="HowTo Guides"></NavBar>
  <v-container fluid style="margin-top: 185px;">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center mb-4">
          <v-btn icon variant="text" size="small" @click="$router.push('/settings')" class="mr-2">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h2>
            <v-icon class="mr-2" color="primary">mdi-book-open-variant</v-icon>
            HowTo Guides
          </h2>
        </div>
      </v-col>
    </v-row>

    <!-- Search -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-text-field
          v-model="searchTerm"
          label="Search guides..."
          prepend-inner-icon="mdi-magnify"
          clearable
          density="compact"
          variant="outlined"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-chip-group v-model="selectedCategory" selected-class="text-primary">
          <v-chip v-for="cat in categories" :key="cat" :value="cat" filter variant="outlined">
            {{ cat }}
          </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <!-- Guides -->
    <v-row>
      <v-col cols="12" v-for="guide in filteredGuides" :key="guide.id">
        <v-card variant="outlined" class="mb-2">
          <v-card-title class="d-flex align-center cursor-pointer" @click="toggleGuide(guide.id)">
            <v-icon class="mr-2" :color="guide.iconColor || 'primary'">{{ guide.icon }}</v-icon>
            <span>{{ guide.title }}</span>
            <v-chip size="x-small" class="ml-3" color="info" variant="tonal">{{ guide.category }}</v-chip>
            <v-spacer />
            <v-icon>{{ expandedGuides.includes(guide.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-card-title>
          <v-card-subtitle v-if="!expandedGuides.includes(guide.id)" class="pb-3">
            {{ guide.summary }}
          </v-card-subtitle>
          <v-expand-transition>
            <div v-if="expandedGuides.includes(guide.id)">
              <v-divider />
              <v-card-text>
                <div v-html="guide.content" class="howto-content"></div>
              </v-card-text>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="filteredGuides.length === 0">
      <v-col cols="12" class="text-center text-grey">
        No guides found matching your search.
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";

export default defineComponent({
  name: 'HowToGuides',
  components: { NavBar },
  data() {
    return {
      searchTerm: '',
      selectedCategory: null,
      expandedGuides: [],
      guides: [
        // ============================================================
        // 1. IMSI
        // ============================================================
        {
          id: 1,
          title: 'IMSI - Cartele SIM (identitate rețea mobilă)',
          summary: 'Ce este un IMSI, cum se gestionează, relațiile cu SIM-uri, tipuri, prețuri și grupuri.',
          category: 'IMSI',
          icon: 'mdi-sim',
          iconColor: 'teal',
          content: `
            <h3>Ce este un IMSI?</h3>
            <p>IMSI (International Mobile Subscriber Identity) este identitatea unică a unei cartele SIM în rețeaua mobilă. Fiecare cartelă fizică SIM are un IMSI unic și un serial (ICCID).</p>

            <h3>Model: Imsi</h3>
            <table class="model-table">
              <tr><th>Câmp</th><th>Descriere</th></tr>
              <tr><td><code>imsi</code></td><td>Numărul IMSI (ex: 226039002139003)</td></tr>
              <tr><td><code>serial</code></td><td>ICCID - seria cartelei fizice (ex: 8940030221250390031)</td></tr>
              <tr><td><code>mno_status_id</code></td><td>Status în rețea: 1=Available, 2=Allocated</td></tr>
              <tr><td><code>network_id</code></td><td>Rețeaua la care aparține</td></tr>
              <tr><td><code>imsi_type_id</code></td><td>Tipul IMSI (legat de prețuri)</td></tr>
              <tr><td><code>reseller_id</code></td><td>Clientul reseller care deține IMSI-ul</td></tr>
            </table>

            <h3>Relații</h3>
            <pre class="diagram">
IMSI
 ├── belongs_to: MnoStatus (available/allocated)
 ├── belongs_to: Network (rețeaua mobilă)
 ├── belongs_to: ImsiType (tip → prețuri)
 ├── has_one: Sim (relație 1:1 — un IMSI are un singur SIM)
 ├── has_many: ImsiGroupMemberships → ImsiGroups
 └── has_many: ImsiPrices (prin imsi_type_id) → Prices
            </pre>

            <h3>Relația IMSI ↔ SIM (one-to-one)</h3>
            <p>Un IMSI are <strong>un singur SIM</strong>. SIM-ul se creează la prima operațiune pe IMSI (sau pe MSISDN). Ulterior, toate serviciile se atașează la acel SIM existent.</p>
            <pre class="diagram">
IMSI (cartela fizică)
 └── has_one: SIM (creat la prima alocare)
      ├── belongs_to: IMSI (imsi_id)
      ├── belongs_to: MSISDN (msisdn_id, adăugat la provizionare dacă lipsea)
      ├── has_many: ClientServices (servicii pe același SIM)
      │    ├── CS: allocate_imsi — alocarea cartelei
      │    ├── CS: msisdn — alocarea numărului de telefon
      │    ├── CS: imsi_subscription — plan billing (voce/SMS/date)
      │    ├── CS: credit — credit suplimentar
      │    └── CS: valability — extensie valabilitate
      └── subscriber_id (ID în MVNO, setat la provizionare)
            </pre>

            <h3>Operații principale</h3>
            <ul>
              <li><strong>Import IMSI-uri</strong>: se importă în bulk din CSV cu serial, imsi, network, imsi_type</li>
              <li><strong>Alocare client</strong>: prima operațiune pe un IMSI available creează SIM-ul, IMSI devine allocated (mno_status_id=2)</li>
              <li><strong>Provizionare</strong>: dacă SIM-ul are doar IMSI → se cere un MSISDN; dacă are doar MSISDN → se cere IMSI/serial</li>
              <li><strong>Servicii pe SIM</strong>: după provizionare se adaugă servicii (imsi_subscription, credit, valability) pe SIM-ul existent</li>
              <li><strong>Eliberare</strong>: la terminarea completă (terminate_subscriber), IMSI-ul revine la available</li>
              <li><strong>Grupuri</strong>: IMSI-urile pot fi organizate în grupuri pentru management mai ușor</li>
            </ul>

            <h3>Cum se creează un SIM?</h3>
            <p>Un SIM se creează automat la <strong>prima operațiune</strong> pe un IMSI sau MSISDN:</p>
            <ul>
              <li><strong>Alocare IMSI</strong> (allocate_imsi): clientul primește un IMSI → se creează SIM cu <code>imsi_id</code> setat, fără MSISDN</li>
              <li><strong>Alocare MSISDN</strong> (msisdn): clientul cumpără/închiriază un număr → se creează SIM cu <code>msisdn_id</code> setat, fără IMSI</li>
              <li><strong>Alocare IMSI+MSISDN</strong> (imsi_msisdn): ambele împreună → SIM cu <code>imsi_id</code> + <code>msisdn_id</code></li>
            </ul>

            <h3>Flux: Alocare → Provizionare → Servicii</h3>
            <pre class="diagram">
FAZA 1: ALOCARE (crearea SIM-ului)
──────────────────────────────────
Varianta A: Clientul primește IMSI
  1. Se alocă IMSI available → se creează SIM (doar imsi_id)
  2. IMSI devine allocated (mno_status_id=2)

Varianta B: Clientul cumpără MSISDN
  1. Se alocă MSISDN → se creează SIM (doar msisdn_id)
  2. MSISDN devine allocated

Varianta C: Ambele împreună (imsi_msisdn)
  1. Se alocă IMSI + MSISDN → SIM cu ambele

FAZA 2: PROVIZIONARE (activare în rețeaua mobilă)
──────────────────────────────────────────────────
SIM are doar IMSI? → sistemul cere un MSISDN pentru provizionare
SIM are doar MSISDN? → sistemul cere un IMSI/serial pentru provizionare
SIM are ambele? → gata de provizionare

  → MvnoProvisioningService.provision_sim(imsi, msisdn)
  → MVNO creează subscriber → callback cu subscriber_id
  → SIM devine active

FAZA 3: SERVICII (adăugare funcționalități pe SIM)
──────────────────────────────────────────────────
Pe SIM-ul activ se pot adăuga servicii (ClientServices):
  • imsi_subscription → plan billing (voce, SMS, date, roaming)
  • credit → credit suplimentar
  • valability → extensie valabilitate
  • alte servicii definite în sistem
Toate CS-urile referă același sim_id.
            </pre>
          `
        },

        // ============================================================
        // 10. SIM - Subscriber în rețeaua mobilă
        // ============================================================
        {
          id: 10,
          title: 'SIM - Subscriber în rețeaua mobilă',
          icon: 'mdi-cellphone-link',
          iconColor: 'deep-purple',
          category: 'SIM',
          summary: 'Ce este un SIM, cum leagă cartela fizică (IMSI) de numărul de telefon (MSISDN), ce servicii mobile oferă și cum se provizionează.',
          content: `
            <h3>Ce este un SIM?</h3>
            <p>SIM-ul este <strong>echivalentul unui subscriber în rețeaua mobilă</strong>. Este entitatea centrală care face legătura între:</p>
            <ul>
              <li><strong>Cartela fizică</strong> (IMSI) — identitatea hardware a SIM-ului în rețea</li>
              <li><strong>Numărul de telefon</strong> (MSISDN) — numărul vizibil al utilizatorului</li>
              <li><strong>Serviciile mobile</strong> — voce, SMS, date, roaming, apeluri internaționale</li>
            </ul>
            <p>Practic, SIM-ul reprezintă "abonatul" din punct de vedere al rețelei mobile. Când un SIM este provizionat, operatorul MVNO creează un subscriber cu toate serviciile configurate.</p>

            <h3>Model: Sim</h3>
            <table class="model-table">
              <tr><th>Câmp</th><th>Descriere</th></tr>
              <tr><td><code>id</code></td><td>ID intern al SIM-ului</td></tr>
              <tr><td><code>imsi_id</code></td><td>Cartela fizică (relație 1:1 cu IMSI)</td></tr>
              <tr><td><code>msisdn_id</code></td><td>Numărul de telefon alocat (opțional)</td></tr>
              <tr><td><code>sim_status_id</code></td><td>Status: not_provisioned, pending, active, expired, suspended, quarantine, terminated</td></tr>
              <tr><td><code>subscriber_id</code></td><td>ID-ul subscriber-ului în sistemul MVNO (setat la provizionare)</td></tr>
              <tr><td><code>subscriberID</code></td><td>ID alternativ returnat de MVNO (format diferit)</td></tr>
              <tr><td><code>last_command_id</code></td><td>Ultima comandă MVNO executată pe acest SIM</td></tr>
            </table>

            <h3>Relații</h3>
            <pre class="diagram">
SIM (subscriber în rețea)
 ├── belongs_to: IMSI (cartela fizică, relație 1:1)
 ├── belongs_to: MSISDN (numărul de telefon, opțional)
 ├── belongs_to: SimStatus (starea curentă)
 ├── has_many: ClientServices (serviciile activate pe acest SIM)
 │    ├── allocate_imsi — alocarea cartelei
 │    ├── msisdn/imsi_msisdn — alocarea numărului
 │    ├── imsi_subscription — abonament (voce, SMS, date)
 │    └── credit/valability — credit adițional
 └── has_many: Commands (comenzile MVNO trimise)
            </pre>

            <h3>SIM = Subscriber MVNO</h3>
            <p>Când un SIM este provizionat (provision_sim sau add_subscription), operatorul MVNO creează un <strong>subscriber</strong> care include:</p>
            <table class="model-table">
              <tr><th>Serviciu rețea</th><th>Descriere</th><th>Configurat prin</th></tr>
              <tr><td><strong>Voce</strong></td><td>Apeluri vocale naționale și internaționale</td><td>i_product (plan billing)</td></tr>
              <tr><td><strong>SMS</strong></td><td>Mesaje text</td><td>i_product (plan billing)</td></tr>
              <tr><td><strong>Date mobile</strong></td><td>Internet 2G/3G/4G/5G</td><td>i_product (plan billing)</td></tr>
              <tr><td><strong>Roaming</strong></td><td>Servicii în rețele vizitate (UE, non-UE)</td><td>Zone roaming configurate în i_product</td></tr>
              <tr><td><strong>Apeluri internaționale</strong></td><td>Apeluri către numere din alte țări</td><td>Destinații incluse în i_product</td></tr>
            </table>
            <p>Toate aceste servicii sunt definite de <code>i_product</code> (billing product) trimis la MVNO prin <code>add_subscription</code>. Fiecare i_product configurează ce include planul: minute voce, SMS-uri, GB date, zone roaming permise, destinații internaționale.</p>

            <h3>Când se creează un SIM?</h3>
            <p>Un SIM se creează la <strong>prima operațiune</strong> pe un IMSI sau pe un MSISDN. Scenarii posibile:</p>
            <pre class="diagram">
SCENARIUL A: Se alocă IMSI unui client
────────────────────────────────────
1. Clientul primește IMSI #1234 (allocate_imsi)
   → Se creează SIM #567: imsi_id=1234, msisdn_id=null
   → SIM status: not_provisioned

2. La provizionare → sistemul cere un MSISDN
   → Se selectează MSISDN #789
   → SIM #567: msisdn_id=789

3. Provizionare MVNO → subscriber creat
   → SIM #567: subscriber_id=SUB_001, status=active

4. Se adaugă servicii pe SIM-ul activ:
   → imsi_subscription (plan: voce + SMS + date + roaming)
   → credit, valability, etc.


SCENARIUL B: Clientul cumpără MSISDN
────────────────────────────────────
1. Clientul cumpără MSISDN #789 (msisdn)
   → Se creează SIM #567: msisdn_id=789, imsi_id=null
   → SIM status: not_provisioned

2. La provizionare → sistemul cere un IMSI/serial
   → Se selectează IMSI #1234
   → SIM #567: imsi_id=1234

3. Provizionare MVNO → subscriber creat
   → SIM #567: subscriber_id=SUB_001, status=active


SCENARIUL C: Ambele împreună (imsi_msisdn)
──────────────────────────────────────────
1. Se alocă IMSI #1234 + MSISDN #789 într-o singură operație
   → Se creează SIM #567: imsi_id=1234, msisdn_id=789
   → Gata de provizionare directă
            </pre>

            <h3>Statusuri SIM</h3>
            <table class="model-table">
              <tr><th>Status</th><th>Descriere</th><th>subscriber_id?</th></tr>
              <tr><td><code>not_provisioned</code></td><td>SIM creat, dar neactivat în rețeaua mobilă</td><td>Nu</td></tr>
              <tr><td><code>pending</code></td><td>Așteptăm confirmare de la MVNO (provizionare sau terminare)</td><td>Posibil</td></tr>
              <tr><td><code>active</code></td><td>Activ în rețea — subscriber funcțional cu toate serviciile</td><td>Da</td></tr>
              <tr><td><code>expired</code></td><td>Expirat — serviciile nu mai sunt active</td><td>Da</td></tr>
              <tr><td><code>suspended</code></td><td>Suspendat temporar (ex: neplată)</td><td>Da</td></tr>
              <tr><td><code>quarantine</code></td><td>În carantină — poate fi reactivat</td><td>Da</td></tr>
              <tr><td><code>terminated</code></td><td>Terminat — subscriber dezactivat din rețea</td><td>Pierdut</td></tr>
            </table>

            <h3>Operații MVNO pe SIM</h3>
            <table class="model-table">
              <tr><th>Operație</th><th>MVNO API Method</th><th>Ce face</th></tr>
              <tr><td>Provizionare</td><td><code>provision_sim</code></td><td>Creează subscriber în rețea (IMSI + MSISDN → activ)</td></tr>
              <tr><td>Adaugă abonament</td><td><code>add_subscription</code></td><td>Activează un plan billing (voce/SMS/date/roaming) via i_product</td></tr>
              <tr><td>Extinde</td><td><code>extend_subscription</code></td><td>Prelungește valabilitatea planului curent</td></tr>
              <tr><td>Suspendare</td><td><code>suspend_subscriber</code></td><td>Dezactivează temporar subscriber-ul</td></tr>
              <tr><td>Reactivare</td><td><code>reactivate_subscriber</code></td><td>Reactivează un subscriber suspendat</td></tr>
              <tr><td>Terminare billing</td><td><code>terminate_billing_subscription</code></td><td>Oprește billing-ul, SIM rămâne provizionat</td></tr>
              <tr><td>Terminare completă</td><td><code>terminate_subscriber</code></td><td>Dezactivează complet subscriber-ul din rețea</td></tr>
            </table>

            <h3>Fișiere implicate</h3>
            <table class="model-table">
              <tr><th>Fișier</th><th>Rol</th></tr>
              <tr><td><code>app/models/sim.rb</code></td><td>Model SIM: relații, scopes, primary_client_service</td></tr>
              <tr><td><code>app/models/imsi.rb</code></td><td>Model IMSI: has_one :sim (relație 1:1)</td></tr>
              <tr><td><code>app/services/mvno_provisioning_service.rb</code></td><td>Toate apelurile MVNO (provision, subscribe, terminate)</td></tr>
              <tr><td><code>home_controller.rb</code> → provision_sim_api</td><td>Provizionare SIM din frontend</td></tr>
              <tr><td><code>home_controller.rb</code> → callback_mvno_*</td><td>Callback-uri de la MVNO (confirmare provizionare/terminare)</td></tr>
              <tr><td><code>src/views/SimsList.vue</code></td><td>Lista SIM-urilor cu acțiuni</td></tr>
              <tr><td><code>src/composables/useGetSimsAPI.js</code></td><td>API calls pentru SIM-uri</td></tr>
            </table>
          `
        },

        // ============================================================
        // 2. MSISDN
        // ============================================================
        {
          id: 2,
          title: 'MSISDN - Numere de telefon',
          summary: 'Ce este un MSISDN, cum se gestionează numerele de telefon, statusuri, clase, prețuri și grupuri.',
          category: 'MSISDN',
          icon: 'mdi-phone-classic',
          iconColor: 'blue',
          content: `
            <h3>Ce este un MSISDN?</h3>
            <p>MSISDN (Mobile Station International Subscriber Directory Number) este numărul de telefon vizibil al utilizatorului (ex: 40783800121). Este resursa pe care clientul o "cumpără" sau "închiriază".</p>

            <h3>Model: Msisdn</h3>
            <table class="model-table">
              <tr><th>Câmp</th><th>Descriere</th></tr>
              <tr><td><code>msisdn</code></td><td>Numărul de telefon (ex: 40783800121)</td></tr>
              <tr><td><code>msisdn_status_id</code></td><td>Status: available(1), reserved(2), pending_payment(3), expired(4), quarantine(5), not_available(6), allocated(7)</td></tr>
              <tr><td><code>msisdn_class_id</code></td><td>Clasa numerelor (obisnuite, gold, platinum, etc.)</td></tr>
              <tr><td><code>network_id</code></td><td>Rețeaua la care aparține</td></tr>
              <tr><td><code>mvno_id</code></td><td>MVNO-ul care gestionează numărul</td></tr>
              <tr><td><code>reserved_until</code></td><td>Data până la care este rezervat</td></tr>
            </table>

            <h3>Relații</h3>
            <pre class="diagram">
MSISDN
 ├── belongs_to: MsisdnStatus (available/reserved/allocated/...)
 ├── belongs_to: MsisdnClass (clasă de numere → prețuri)
 ├── belongs_to: Network (rețeaua mobilă)
 ├── belongs_to: Mvno (own_network sau other_network)
 ├── has_many: Sims (un MSISDN are de obicei un SIM, dar poate avea istorice)
 ├── has_many: MsisdnGroupMemberships → MsisdnGroups
 └── has_many: MsisdnPrices (prin msisdn_class_id) → Prices
            </pre>

            <h3>Statusuri MSISDN</h3>
            <table class="model-table">
              <tr><th>ID</th><th>Nume</th><th>Descriere</th></tr>
              <tr><td>1</td><td>available</td><td>Disponibil pentru alocare</td></tr>
              <tr><td>2</td><td>reserved</td><td>Rezervat temporar (reserved_until)</td></tr>
              <tr><td>3</td><td>pending_payment</td><td>Așteaptă plata</td></tr>
              <tr><td>4</td><td>expired</td><td>Expirat</td></tr>
              <tr><td>5</td><td>quarantine</td><td>În carantină (poate fi recuperat)</td></tr>
              <tr><td>6</td><td>not_available</td><td>Indisponibil</td></tr>
              <tr><td>7</td><td>allocated</td><td>Alocat unui client</td></tr>
            </table>

            <h3>Operații principale</h3>
            <ul>
              <li><strong>Buy/Rent</strong>: din MSISDN Management, clientul cumpără/închiriază un număr → se creează CS + SIM</li>
              <li><strong>Rezervare</strong>: <code>msisdn.reserve(time)</code> - setează reserved_until și status=reserved</li>
              <li><strong>Provizionare</strong>: după plată, SIM-ul se provizionează în MVNO cu IMSI + MSISDN</li>
              <li><strong>Portabilitate</strong>: transfer MSISDN de la alt operator</li>
              <li><strong>Grupuri</strong>: organizare în grupuri per client</li>
            </ul>

            <h3>Clase și Prețuri</h3>
            <p>Prețul unui MSISDN depinde de <strong>MsisdnClass</strong> (numere obișnuite, gold, platinum). Fiecare clasă are prețuri definite în <code>MsisdnPrice</code> cu property_type (buy/rent/transfer) și billing_period.</p>
          `
        },

        // ============================================================
        // 3. Services
        // ============================================================
        {
          id: 3,
          title: 'Services - Servicii (configurare și tipuri)',
          summary: 'Cum funcționează serviciile: tipuri (imsi, msisdn, imsi_subscription, client), billing, prețuri, MVNO operations.',
          category: 'Services',
          icon: 'mdi-cogs',
          iconColor: 'purple',
          content: `
            <h3>Ce este un Service?</h3>
            <p>Un Service definește un tip de abonament sau produs pe care clientul îl poate activa. Este "template-ul" - configurarea. Când un client activează un serviciu, se creează un <strong>ClientService</strong>.</p>

            <h3>Model: Service</h3>
            <table class="model-table">
              <tr><th>Câmp</th><th>Descriere</th></tr>
              <tr><td><code>name</code></td><td>Numele serviciului</td></tr>
              <tr><td><code>service_type_id</code></td><td>Tipul: imsi, msisdn, imsi_msisdn, imsi_subscription, client, credit, valability</td></tr>
              <tr><td><code>billing_type_id</code></td><td>postpaid, prepaid, onetime, free</td></tr>
              <tr><td><code>billing_period_id</code></td><td>Perioada: 1Hour, 1Day, 1Week, 1Month, etc.</td></tr>
              <tr><td><code>validity</code></td><td>Valabilitatea în zile</td></tr>
              <tr><td><code>mvno_id</code></td><td>MVNO-ul asociat (opțional)</td></tr>
              <tr><td><code>allow_multiple</code></td><td>Permite mai multe CS active simultan</td></tr>
            </table>

            <h3>Tipuri de servicii (ServiceType)</h3>
            <table class="model-table">
              <tr><th>Tip</th><th>Ce face</th><th>Creează SIM?</th></tr>
              <tr><td><code>imsi</code></td><td>Alocă un IMSI pe un SIM</td><td>Da (IMSI only)</td></tr>
              <tr><td><code>msisdn</code></td><td>Alocă un MSISDN pe un SIM</td><td>Da (MSISDN only)</td></tr>
              <tr><td><code>imsi_msisdn</code></td><td>Alocă IMSI + MSISDN pe un SIM</td><td>Da (ambele)</td></tr>
              <tr><td><code>imsi_subscription</code></td><td>Adaugă un abonament/tarif pe un SIM existent</td><td>Nu (se atașează la SIM)</td></tr>
              <tr><td><code>client</code></td><td>Serviciu fără SIM (la nivel de client)</td><td>Nu</td></tr>
              <tr><td><code>credit</code></td><td>Reîncărcare credit</td><td>Nu</td></tr>
              <tr><td><code>valability</code></td><td>Extensie valabilitate</td><td>Nu</td></tr>
            </table>

            <h3>Relații</h3>
            <pre class="diagram">
Service
 ├── belongs_to: ServiceType (imsi/msisdn/imsi_subscription/...)
 ├── belongs_to: BillingType (postpaid/prepaid/onetime/free)
 ├── belongs_to: BillingPeriod (1Month/1Year/...)
 ├── belongs_to: Mvno (opțional)
 ├── has_many: ServicePrices → Price (prețuri active)
 ├── has_many: ClientServices (instanțe active)
 ├── has_many: ServiceMvnoOperations (ce comenzi MVNO se trimit)
 ├── has_many: ServiceQuarantineDurations (durate carantină)
 └── has_many: ClientServicesLimitations (limitări per client)
            </pre>

            <h3>Prețuri (ServicePrice → Price)</h3>
            <p>Fiecare serviciu are un preț activ definit în <code>ServicePrice</code> care referă un <code>Price</code>. Prețul conține <code>amount</code>, <code>currency</code>, <code>credit</code>, <code>validity</code>.</p>
            <p>Metoda <code>service.current_price</code> returnează ultimul preț activ.</p>

            <h3>MVNO Operations (ServiceMvnoOperation)</h3>
            <p>Definesc CE comandă MVNO se trimite când se face o operație pe serviciu:</p>
            <table class="model-table">
              <tr><th>Operation Type</th><th>Când se declanșează</th><th>Exemplu API Method</th></tr>
              <tr><td><code>new</code></td><td>La crearea CS</td><td>add_subscription, provision_sim</td></tr>
              <tr><td><code>extend</code></td><td>La extindere</td><td>extend_subscription</td></tr>
              <tr><td><code>suspend</code></td><td>La suspendare</td><td>suspend_subscriber</td></tr>
              <tr><td><code>reactivate</code></td><td>La reactivare</td><td>reactivate_subscriber</td></tr>
              <tr><td><code>terminate_subscription</code></td><td>La terminare</td><td>terminate_billing_subscription</td></tr>
            </table>
          `
        },

        // ============================================================
        // 4. Products & Distributor Products
        // ============================================================
        {
          id: 4,
          title: 'Products & Distributor Products - Produse de billing',
          summary: 'Cum funcționează produsele, alocarea la distribuitori, și legătura cu billing-ul MVNO prin billing_i_product.',
          category: 'Products',
          icon: 'mdi-package-variant',
          iconColor: 'orange',
          content: `
            <h3>Ce este un Product?</h3>
            <p>Un Product reprezintă un produs de billing din sistemul MVNO. Câmpul cheie este <code>billing_i_product</code> care este ID-ul produsului în platforma de billing a MVNO.</p>

            <h3>Model: Product</h3>
            <table class="model-table">
              <tr><th>Câmp</th><th>Descriere</th></tr>
              <tr><td><code>name</code></td><td>Numele descriptiv (ex: "rslBenefitoVoiceSmsDataCustomers")</td></tr>
              <tr><td><code>billing_i_product</code></td><td>ID-ul produsului în billing MVNO (ex: 2, 14, 29)</td></tr>
              <tr><td><code>reseller_id</code></td><td>Clientul care a creat produsul</td></tr>
            </table>

            <h3>Model: DistributorProduct</h3>
            <p>Leagă un <strong>Product</strong> de un <strong>Client distribuitor</strong>. Controlează ce produse de billing are voie un distribuitor să folosească.</p>
            <table class="model-table">
              <tr><th>Câmp</th><th>Descriere</th></tr>
              <tr><td><code>client_id</code></td><td>Distribuitorul (Client cu is_distributor=true)</td></tr>
              <tr><td><code>product_id</code></td><td>Produsul alocat</td></tr>
            </table>

            <h3>Relații</h3>
            <pre class="diagram">
Product
 ├── belongs_to: Client (reseller - creatorul)
 ├── has_many: DistributorProducts
 │     └── belongs_to: Client (distribuitorul)
 └── has_many: DistributorServiceProducts
       └── leagă Product + Service + Distributor + MVNO

Client (distributor)
 ├── has_many: DistributorProducts → Products
 └── has_many: DistributorServiceProducts
            </pre>

            <h3>De ce contează?</h3>
            <p>Când se adaugă un <code>DistributorServiceProduct</code> (mapping service → product → distributor), dialogul de adăugare listează DOAR produsele alocate distribuitorului prin <code>DistributorProduct</code>.</p>
            <p><strong>Dacă un produs nu e alocat distribuitorului, nu va apărea în lista de produse disponibile!</strong></p>

            <h3>Pași pentru configurare</h3>
            <ol>
              <li>Creează Product cu <code>billing_i_product</code> corect din MVNO</li>
              <li>Alocă Product la Distribuitor: Settings → Products → Distributor Products</li>
              <li>Folosește Product-ul în DistributorServiceProducts pentru mapping</li>
            </ol>
          `
        },

        // ============================================================
        // 5. Client Services
        // ============================================================
        {
          id: 5,
          title: 'Client Services - Servicii activate pe clienți',
          summary: 'Cum funcționează ClientService: ciclul de viață, statusuri, plăți, provizionare, carantină și terminare.',
          category: 'Client Services',
          icon: 'mdi-account-cog',
          iconColor: 'indigo',
          content: `
            <h3>Ce este un ClientService?</h3>
            <p>Un ClientService este o <strong>instanță</strong> a unui Service activată pentru un client. Este legătura concretă: "clientul X are serviciul Y activ de la data A până la data B pe SIM-ul Z".</p>

            <h3>Model: ClientService</h3>
            <table class="model-table">
              <tr><th>Câmp</th><th>Descriere</th></tr>
              <tr><td><code>client_id</code></td><td>Clientul care deține serviciul</td></tr>
              <tr><td><code>service_id</code></td><td>Serviciul activat</td></tr>
              <tr><td><code>sim_id</code></td><td>SIM-ul asociat (opțional, depinde de service_type)</td></tr>
              <tr><td><code>client_service_status_id</code></td><td>Status: active, pending_payment, expired, suspended, quarantine, terminated, refunded</td></tr>
              <tr><td><code>start_date</code></td><td>Data de start</td></tr>
              <tr><td><code>expiration_date</code></td><td>Data de expirare</td></tr>
              <tr><td><code>autorenew</code></td><td>Reînnoire automată</td></tr>
              <tr><td><code>paid_to_client_id</code></td><td>Clientul către care se plătește</td></tr>
              <tr><td><code>quarantine_expires_at</code></td><td>Data expirare carantină</td></tr>
              <tr><td><code>termination_reason</code></td><td>Motivul terminării</td></tr>
            </table>

            <h3>Relații</h3>
            <pre class="diagram">
ClientService
 ├── belongs_to: Client (proprietarul)
 ├── belongs_to: Service (template-ul)
 ├── belongs_to: ClientServiceStatus
 ├── belongs_to: Sim (opțional - SIM-ul fizic)
 ├── belongs_to: PaidToClient (cine primește banii)
 ├── belongs_to: ParentClientService (extensii/cascade)
 ├── has_one: OrderClientService → Order
 ├── has_many: Extensions (CS copii)
 ├── has_many: ClientServiceQuarantineDurations
 └── has_many: CsValidityContributions
            </pre>

            <h3>Ciclul de viață</h3>
            <pre class="diagram">
                                     ┌──────────┐
                                     │ REFUNDED │
                                     └────▲─────┘
                                          │
┌─────────────────┐    plată    ┌────────┐│   suspend    ┌───────────┐
│ PENDING_PAYMENT │───────────→│ ACTIVE │├──────────────→│ SUSPENDED │
└─────────────────┘             └───┬────┘│              └─────┬─────┘
                                    │     │   reactivate       │
                                    │     └────────────────────┘
                               expirare
                                    │
                                    ▼
                              ┌─────────┐    carantină    ┌────────────┐    expiră    ┌────────────┐
                              │ EXPIRED │────────────────→│ QUARANTINE │────────────→│ TERMINATED │
                              └─────────┘                 └────────────┘              └────────────┘
            </pre>

            <h3>Tipuri de CS per ServiceType</h3>
            <table class="model-table">
              <tr><th>ServiceType</th><th>Creează SIM?</th><th>Comportament la carantină</th></tr>
              <tr><td><code>msisdn</code></td><td>Da</td><td>CASCADE - toate CS pe SIM intră în carantină</td></tr>
              <tr><td><code>imsi_msisdn</code></td><td>Da</td><td>CASCADE</td></tr>
              <tr><td><code>imsi</code></td><td>Da</td><td>CASCADE</td></tr>
              <tr><td><code>imsi_subscription</code></td><td>Nu (se atașează)</td><td>Individual</td></tr>
              <tr><td><code>client</code></td><td>Nu</td><td>Individual (fără SIM)</td></tr>
              <tr><td><code>credit/valability</code></td><td>Nu</td><td>Skip (nu intră în carantină)</td></tr>
            </table>

            <h3>Operații principale</h3>
            <ul>
              <li><strong>Add</strong>: creează CS + SIM (dacă e nevoie) + provizionare MVNO</li>
              <li><strong>Pay</strong>: plătește CS pending_payment → active</li>
              <li><strong>Suspend</strong>: suspendă temporar (MVNO suspend_subscriber)</li>
              <li><strong>Reactivate</strong>: reactivează din suspended</li>
              <li><strong>Extend</strong>: extinde valabilitatea (MVNO extend_subscription)</li>
              <li><strong>Terminate</strong>: terminare manuală (MVNO terminate_subscriber)</li>
              <li><strong>Refund</strong>: rambursare (doar din status active, luna curentă)</li>
              <li><strong>Renewal</strong>: reînnoire automată la expirare (RenewalProcessorService)</li>
            </ul>
          `
        },

        // ============================================================
        // 6. Distributor Service Products
        // ============================================================
        {
          id: 6,
          title: 'Distributor Service Products - Mapping serviciu → tarif billing',
          summary: 'Cum se leagă un serviciu de un tarif de billing (i_product) per distribuitor și MVNO.',
          category: 'Services',
          icon: 'mdi-link-variant',
          iconColor: 'deep-purple',
          content: `
            <h3>Ce este un DistributorServiceProduct?</h3>
            <p>Este mapping-ul care leagă un <strong>Service</strong> de un <strong>Product</strong> (cu billing_i_product) pentru un anumit <strong>Distribuitor</strong> și opțional un <strong>MVNO</strong>.</p>
            <p>Când se adaugă un serviciu de tip <code>imsi_subscription</code> pe un SIM, sistemul caută acest mapping pentru a determina ce <code>i_product</code> să trimită la MVNO.</p>

            <h3>Model: DistributorServiceProduct</h3>
            <table class="model-table">
              <tr><th>Câmp</th><th>Descriere</th></tr>
              <tr><td><code>service_id</code></td><td>Serviciul</td></tr>
              <tr><td><code>distributor_id</code></td><td>Distribuitorul (Client cu is_distributor=true)</td></tr>
              <tr><td><code>product_id</code></td><td>Produsul (conține billing_i_product)</td></tr>
              <tr><td><code>mvno_id</code></td><td>MVNO-ul specific (sau NULL = orice MVNO)</td></tr>
              <tr><td><code>active</code></td><td>Activ/Inactiv</td></tr>
            </table>

            <h3>Cum se găsește i_product (find_i_product)</h3>
            <p>Metoda <code>DistributorServiceProduct.find_i_product(service_id, client, mvno_id)</code> caută ierarhic:</p>
            <ol>
              <li>Distributor direct (cu mvno_id specific)</li>
              <li>Distributor direct (fără mvno_id - fallback)</li>
              <li>Părintele distribuitorului (cu mvno_id)</li>
              <li>Părintele distribuitorului (fără mvno_id)</li>
              <li>Și tot așa în sus pe ierarhie până la Root</li>
            </ol>

            <h3>Diagrama completă</h3>
            <pre class="diagram">
Client (subclient)
 └── parent: Distribuitor (is_distributor=true)
       ├── DistributorProduct: Product (billing_i_product=2)
       ├── DistributorServiceProduct:
       │     Service + Product + MVNO
       │     → i_product = product.billing_i_product
       └── parent: Root (is_distributor=true)
             └── DistributorServiceProduct (fallback)
            </pre>

            <h3>Prerequisite</h3>
            <ol>
              <li><strong>Product</strong> trebuie creat cu <code>billing_i_product</code> corect</li>
              <li><strong>DistributorProduct</strong> trebuie să aloce Product-ul distribuitorului (altfel nu apare în dialog!)</li>
              <li><strong>ServiceMvnoOperation</strong> trebuie configurată (new → add_subscription)</li>
              <li>Abia apoi <strong>DistributorServiceProduct</strong> leagă totul</li>
            </ol>
          `
        },

        // ============================================================
        // 7. Billing - Cum aloci un tarif
        // ============================================================
        {
          id: 7,
          title: 'Cum aloci un tarif de billing (i_product) unui SIM',
          summary: 'Ghid pas cu pas: de la crearea serviciului până la implementarea tarifului pe SIM-urile dorite.',
          category: 'Billing',
          icon: 'mdi-credit-card-settings-outline',
          iconColor: 'success',
          content: `
            <h3>Prezentare generală</h3>
            <p>Când adaugi un serviciu de tip <strong>imsi_subscription</strong> pe un SIM, sistemul trimite către MVNO un <code>i_product</code> care definește metoda de taxare și creditul în billing.</p>

            <h3>Pasul 1: Creează Serviciul</h3>
            <p><strong>Settings → Services</strong></p>
            <ul>
              <li>Adaugă un serviciu nou cu tipul <code>imsi_subscription</code></li>
              <li>Configurează prețul, perioada de billing și valabilitatea</li>
            </ul>

            <h3>Pasul 2: Creează Produsul de Billing</h3>
            <p><strong>Settings → Products</strong></p>
            <ul>
              <li>Creează un produs cu <code>billing_i_product</code> setat la valoarea din MVNO</li>
            </ul>

            <h3>Pasul 3: Alocă Produsul la Distribuitor</h3>
            <p><strong>Settings → Products → Distributor Products</strong></p>
            <ul>
              <li>Leagă produsul de distribuitorul care va folosi acest tarif</li>
              <li><em>Fără acest pas, produsul nu apare în lista disponibilă!</em></li>
            </ul>

            <h3>Pasul 4: Configurează Operația MVNO</h3>
            <p><strong>Settings → Services → Service MVNO Operations</strong></p>
            <ul>
              <li>Service + Operation Type: <code>new</code> + MVNO API Method: <code>add_subscription</code></li>
            </ul>

            <h3>Pasul 5: Mapează i_product</h3>
            <p><strong>Settings → Services → Distributor Service Products</strong></p>
            <ul>
              <li>Distributor + Service + Product (cu billing_i_product) + MVNO</li>
            </ul>

            <h3>Pasul 6: Adaugă Serviciul pe SIM</h3>
            <p>Din <strong>Client Services → Add New</strong> sau din <strong>SIM Details</strong></p>
            <p>Sistemul va trimite: <code>add_subscription(serial, i_product, credit, validity)</code></p>

            <h3>Diagrama fluxului</h3>
            <pre class="diagram">
Service (imsi_subscription)
    ├── ServiceMvnoOperation (new → add_subscription)
    ├── DistributorServiceProduct (service + distributor + product + mvno)
    │       └── Product (billing_i_product = 2)
    │               └── DistributorProduct (product alocat distribuitorului)
    └── Adaugă CS pe SIM
            └── MVNO API: add_subscription(serial, i_product=2, credit, validity)
            </pre>

            <h3>Troubleshooting</h3>
            <ul>
              <li><strong>i_product este null</strong>: Verifică DistributorServiceProduct pentru service + distributor + MVNO</li>
              <li><strong>Produsul nu apare în dialog</strong>: Verifică DistributorProduct (produsul alocat distribuitorului)</li>
              <li><strong>Comanda MVNO nu se trimite</strong>: Verifică ServiceMvnoOperation (new + add_subscription)</li>
              <li><strong>MVNO returnează eroare</strong>: Verifică dacă i_product-ul există în billing MVNO</li>
            </ul>
          `
        },

        // ============================================================
        // 8. Relații între entități
        // ============================================================
        {
          id: 8,
          title: 'Relațiile complete dintre entități',
          summary: 'Diagrama completă a relațiilor: Client → Service → SIM → IMSI → MSISDN → MVNO → Billing.',
          category: 'Arhitectura',
          icon: 'mdi-sitemap',
          iconColor: 'red',
          content: `
            <h3>Diagrama completă</h3>
            <pre class="diagram">
┌─────────────────────────────────────────────────────────────────────┐
│                         HIERARHIA CLIENȚI                          │
│                                                                     │
│  Root (is_distributor=true)                                         │
│   └── Distribuitor (is_distributor=true)                            │
│        ├── DistributorProducts → Products (billing_i_product)       │
│        ├── DistributorServiceProducts → Service + Product + MVNO    │
│        └── Subclient (is_distributor=false)                         │
│             └── ClientServices (instanțe de servicii)               │
└─────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────┐
│                        CONFIGURARE SERVICII                         │
│                                                                     │
│  Service                                                            │
│   ├── ServiceType (imsi/msisdn/imsi_subscription/client/...)        │
│   ├── BillingType (postpaid/prepaid/onetime/free)                   │
│   ├── BillingPeriod (1Month/1Year/...)                              │
│   ├── ServicePrice → Price (amount, credit, validity)               │
│   ├── ServiceMvnoOperation                                          │
│   │    ├── ServiceOperationType (new/extend/suspend/terminate)      │
│   │    └── MvnoApiMethod (add_subscription/provision_sim/...)       │
│   └── ServiceQuarantineDuration (durate carantină per property_type)│
└─────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────┐
│                        RESURSE FIZICE                                │
│                                                                     │
│  IMSI (cartela fizică)          MSISDN (număr telefon)              │
│   ├── ImsiType → ImsiPrice       ├── MsisdnClass → MsisdnPrice     │
│   ├── Network                    ├── Network                        │
│   ├── MnoStatus (avail/alloc)    ├── MsisdnStatus (7 statusuri)    │
│   ├── ImsiGroups                 ├── Mvno (own/other network)       │
│   └── has_one: SIM (1:1)        └── MsisdnGroups                   │
│                                                                     │
│  SIM (legătura fizică — creat o singură dată per IMSI)              │
│   ├── belongs_to: IMSI (imsi_id, relație 1:1)                      │
│   ├── belongs_to: MSISDN (msisdn_id, opțional)                     │
│   ├── sim_status (active/not_provisioned/terminated/...)            │
│   ├── subscriber_id (ID în MVNO)                                    │
│   └── has_many: ClientServices (multiple servicii pe același SIM)   │
└─────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────┐
│                        FLUX OPERAȚIONAL                              │
│                                                                     │
│  1. Client cere serviciu                                            │
│  2. Se creează ClientService (pending_payment)                      │
│  3. Se creează Order + OrderClientService                           │
│  4. Plata → CS devine active                                        │
│  5. Dacă IMSI nu are SIM → se creează SIM (1:1 cu IMSI + MSISDN)   │
│     Dacă IMSI are deja SIM → CS se atașează la SIM-ul existent     │
│  6. Se provizionează SIM în MVNO (provision_sim/add_subscription)   │
│  7. Callback MVNO → SIM active, subscriber_id setat                 │
│  8. Servicii ulterioare → se adaugă pe SIM-ul existent              │
│  9. La expirare → RenewalProcessorService                           │
│     a. autorenew=true → plată → extend                              │
│     b. autorenew=false → quarantine → terminate                     │
└─────────────────────────────────────────────────────────────────────┘
            </pre>

            <h3>Relații cheie</h3>
            <table class="model-table">
              <tr><th>De la</th><th>La</th><th>Prin</th><th>Scop</th></tr>
              <tr><td>Client</td><td>Service</td><td>ClientService</td><td>Ce servicii are clientul</td></tr>
              <tr><td>ClientService</td><td>Sim</td><td>sim_id</td><td>Pe ce SIM e serviciul</td></tr>
              <tr><td>Imsi</td><td>Sim</td><td>has_one (1:1)</td><td>Un IMSI = un singur SIM</td></tr>
              <tr><td>Sim</td><td>Imsi</td><td>imsi_id</td><td>Ce cartelă fizică</td></tr>
              <tr><td>Sim</td><td>Msisdn</td><td>msisdn_id</td><td>Ce număr de telefon</td></tr>
              <tr><td>Service</td><td>MvnoApiMethod</td><td>ServiceMvnoOperation</td><td>Ce comandă MVNO se trimite</td></tr>
              <tr><td>Service</td><td>Product</td><td>DistributorServiceProduct</td><td>Ce tarif billing (i_product)</td></tr>
              <tr><td>Client</td><td>Product</td><td>DistributorProduct</td><td>Ce produse poate folosi</td></tr>
            </table>
          `
        },

        // ============================================================
        // 9. Terminare abonament SIM (cu MVNO) - SIM rămâne provizionat
        // ============================================================
        {
          id: 9,
          title: 'Terminare abonament SIM - cu apel MVNO, SIM rămâne provizionat',
          icon: 'mdi-sim-off',
          iconColor: 'red',
          category: 'Operațiuni',
          summary: 'Cum se termină un Client Service pe un SIM, cu apel la MVNO API (terminate_billing_subscription), dar fără a deproviziona SIM-ul din rețeaua mobilă.',
          content: `
            <h3>Concept</h3>
            <p>Există două tipuri de terminare a unui abonament pe SIM:</p>
            <ol>
              <li><strong>terminate_subscriber</strong> (deprovizionare completă) — SIM-ul este dezactivat din rețeaua mobilă, IMSI și MSISDN sunt eliberate. SIM-ul devine <code>terminated</code>.</li>
              <li><strong>terminate_billing_subscription</strong> (terminare billing) — se oprește doar abonamentul de billing la MVNO, dar SIM-ul rămâne provizionat în rețea. SIM-ul rămâne <code>active</code> sau trece în <code>not_provisioned</code>.</li>
            </ol>
            <p>Acest ghid descrie scenariul <strong>#2</strong>: terminarea unui Client Service (ex: <code>imsi_subscription</code>) cu apel MVNO <code>terminate_billing_subscription</code>, păstrând SIM-ul provizionat.</p>

            <h3>Când se folosește</h3>
            <ul>
              <li>Clientul vrea să schimbe planul/abonamentul fără a pierde conectivitatea SIM</li>
              <li>Se termină un serviciu adițional (imsi_subscription) dar SIM-ul are alte servicii active</li>
              <li>Se face upgrade/downgrade de plan: se termină CS vechi, se creează CS nou cu alt i_product</li>
              <li>SIM-ul trebuie să rămână activ în rețeaua mobilă pentru alte servicii</li>
            </ul>

            <h3>Fluxul complet pas cu pas</h3>
            <pre class="diagram">
┌─────────────────────────────────────────────────────────────────┐
│  TERMINARE ABONAMENT (BILLING ONLY) - SIM RĂMÂNE PROVIZIONAT   │
├─────────────────────────────────────────────────────────────────┤
│                                                                 │
│  1. User apasă "Terminate" pe un CS din ClientServicesList      │
│     └── confirmTerminate(item) → actionDialog = true            │
│                                                                 │
│  2. User confirmă → executeAction()                             │
│     └── callActionAPI(cs_id, 'terminate')                       │
│         └── terminateClientService(token, cs_id)                │
│             └── POST /home/terminate_client_service_api         │
│                 { client_service_id: cs_id }                    │
│                                                                 │
│  3. Backend: terminate_client_service_api                       │
│     ├── Verifică permisiuni (owner sau parent client)           │
│     ├── Verifică status (doar active/expired)                   │
│     ├── Protecție AlocareSim (nu se termină dacă are CS active) │
│     ├── Cascade check (msisdn/imsi_msisdn → cascade)           │
│     │                                                           │
│     │  Pentru servicii NON-cascade (ex: imsi_subscription):     │
│     ├── Caută ServiceMvnoOperation pentru terminate_subscription │
│     │   └── ServiceMvnoOperation.find_for_service(              │
│     │         service_id, 'terminate_subscription')             │
│     │                                                           │
│     ├── Dacă metoda MVNO = 'terminate_billing_subscription':    │
│     │   └── MvnoProvisioningService                             │
│     │       .terminate_billing_subscription(sim_id, cs_id)      │
│     │       ├── Creează Command (TERMINATE_BILLING_ACCOUNT)     │
│     │       ├── POST la MVNO: { imsi, msisdn, reseller_id }    │
│     │       ├── Endpoint: mvno_api_terminate_billing_endpoint   │
│     │       └── Răspuns 2xx → success                           │
│     │                                                           │
│     │   ⚠️ SIM-ul NU se termină!                                │
│     │   ⚠️ MSISDN-ul NU se eliberează!                          │
│     │   ✅ CS devine terminated                                  │
│     │                                                           │
│     ├── Dacă metoda MVNO = 'terminate_subscriber':              │
│     │   └── MvnoProvisioningService                             │
│     │       .terminate_subscriber(user_id, sim, imsi, msisdn)   │
│     │       ├── Creează Command (TERMINATE_SUBSCRIPTION)        │
│     │       ├── POST la MVNO: { imsi, msisdn, subscriberID }   │
│     │       ├── SIM → terminated, MSISDN → available            │
│     │       └── ⚠️ SIM NU mai rămâne provizionat!               │
│     │                                                           │
│     └── CS.update!(status: terminated, terminated_at: now)      │
│         └── History.log_action('terminate', cs)                 │
│                                                                 │
│  4. Răspuns la frontend: { error: false, message: "Success" }   │
│     └── Refresh lista CS                                        │
└─────────────────────────────────────────────────────────────────┘
            </pre>

            <h3>Configurarea ServiceMvnoOperation</h3>
            <p>Pentru ca terminarea să apeleze <code>terminate_billing_subscription</code> (și NU <code>terminate_subscriber</code>), trebuie configurat corect în <strong>Settings → Services → Service MVNO Operations</strong>:</p>
            <table class="model-table">
              <tr><th>Câmp</th><th>Valoare</th><th>Explicație</th></tr>
              <tr><td>Service</td><td>serviciul dorit (ex: imsi_subscription)</td><td>Serviciul pentru care se configurează terminarea</td></tr>
              <tr><td>Operation Type</td><td><code>terminate_subscription</code></td><td>Tipul de operație = terminare</td></tr>
              <tr><td>MVNO API Method</td><td><code>terminate_billing_subscription</code></td><td>Metoda MVNO care oprește DOAR billing-ul</td></tr>
            </table>
            <p><strong>Important:</strong> Dacă se configurează <code>terminate_subscriber</code> în loc de <code>terminate_billing_subscription</code>, SIM-ul va fi complet deprovizionat!</p>

            <h3>Diferențe între metodele MVNO</h3>
            <table class="model-table">
              <tr><th>Aspect</th><th>terminate_billing_subscription</th><th>terminate_subscriber</th></tr>
              <tr><td>Use case MVNO</td><td>TERMINATE_BILLING_ACCOUNT</td><td>TERMINATE_SUBSCRIPTION</td></tr>
              <tr><td>Ce trimite la MVNO</td><td>imsi, msisdn, reseller_id</td><td>imsi, msisdn, subscriberID</td></tr>
              <tr><td>Endpoint (Setting)</td><td>mvno_api_terminate_billing_endpoint</td><td>mvno_api_terminate_endpoint</td></tr>
              <tr><td>SIM status după</td><td><strong>Neschimbat</strong> (rămâne active)</td><td>terminated</td></tr>
              <tr><td>MSISDN status după</td><td><strong>Neschimbat</strong></td><td>available (eliberat)</td></tr>
              <tr><td>IMSI status după</td><td><strong>Neschimbat</strong></td><td>available (eliberat)</td></tr>
              <tr><td>subscriber_id</td><td><strong>Păstrat</strong> pe SIM</td><td>Pierdut (SIM terminat)</td></tr>
              <tr><td>Reconectare</td><td>Posibilă (add_subscription)</td><td>Necesită re-provizionare completă</td></tr>
            </table>

            <h3>Ce se întâmplă în baza de date</h3>
            <p>La <code>terminate_billing_subscription</code>:</p>
            <table class="model-table">
              <tr><th>Entitate</th><th>Înainte</th><th>După</th></tr>
              <tr><td>ClientService</td><td>status: <code>active</code></td><td>status: <code>terminated</code>, terminated_at setat</td></tr>
              <tr><td>SIM</td><td>status: <code>active</code></td><td>status: <code>active</code> (neschimbat!)</td></tr>
              <tr><td>MSISDN</td><td>status: <code>allocated</code></td><td>status: <code>allocated</code> (neschimbat!)</td></tr>
              <tr><td>IMSI</td><td>mno_status: <code>allocated</code></td><td>mno_status: <code>allocated</code> (neschimbat!)</td></tr>
              <tr><td>Command</td><td>—</td><td>NOU: action=Terminate, use_case=TERMINATE_BILLING_ACCOUNT</td></tr>
              <tr><td>History</td><td>—</td><td>NOU: action=terminate, cs_id</td></tr>
            </table>

            <h3>Flux alternativ: terminate_sim_subscription_api (din SimsList)</h3>
            <p>Există și un endpoint separat <code>terminate_sim_subscription_api</code> care primește <code>sim_id</code> și <code>deprovision</code>:</p>
            <ul>
              <li><code>deprovision=true</code>: apelează <code>terminate_subscriber</code> → SIM pending → callback → SIM terminated, IMSI/MSISDN eliberate</li>
              <li><code>deprovision=false</code>: termină CS dar SIM rămâne provizionat (fără apel MVNO!)</li>
            </ul>
            <p><strong>Atenție:</strong> Varianta cu <code>deprovision=false</code> nu apelează MVNO deloc — doar termină CS-ul și marchează SIM ca terminated. Nu este ideală dacă vrei să oprești billing-ul la MVNO.</p>

            <h3>Scenariul upgrade de plan (schimbare i_product)</h3>
            <pre class="diagram">
┌─────────────────────────────────────────────────────────────────┐
│  UPGRADE PLAN: imsi_subscription vechi → imsi_subscription nou  │
├─────────────────────────────────────────────────────────────────┤
│                                                                 │
│  1. Terminate CS vechi (imsi_subscription cu i_product=2)       │
│     └── terminate_billing_subscription → billing oprit la MVNO  │
│     └── CS vechi → terminated                                   │
│     └── SIM rămâne active + subscriber_id păstrat               │
│                                                                 │
│  2. Adaugă CS nou pe același SIM                                │
│     └── AddNewClientService pe sim_id existent                  │
│     └── imsi_subscription cu i_product=5 (plan nou)             │
│     └── ServiceMvnoOperation: new → add_subscription            │
│     └── MVNO primește add_subscription cu noul i_product        │
│                                                                 │
│  Rezultat: SIM activ, plan schimbat, fără re-provizionare      │
└─────────────────────────────────────────────────────────────────┘
            </pre>

            <h3>Fișiere implicate</h3>
            <table class="model-table">
              <tr><th>Fișier</th><th>Rol</th></tr>
              <tr><td><code>home_controller.rb</code> → terminate_client_service_api</td><td>Endpoint principal de terminare CS (linia ~7875)</td></tr>
              <tr><td><code>home_controller.rb</code> → terminate_sim_subscription_api</td><td>Endpoint alternativ cu sim_id + deprovision flag (linia ~4903)</td></tr>
              <tr><td><code>mvno_provisioning_service.rb</code> → terminate_billing_subscription</td><td>Apel MVNO pentru oprire billing (linia ~542)</td></tr>
              <tr><td><code>mvno_provisioning_service.rb</code> → terminate_subscriber</td><td>Apel MVNO pentru deprovizionare completă (linia ~654)</td></tr>
              <tr><td><code>home_controller.rb</code> → callback_mvno_terminate_subscription</td><td>Callback de la MVNO la terminate_subscriber (linia ~6962)</td></tr>
              <tr><td><code>useGetAllocatedMsisdnsAPI.js</code> → terminateClientService</td><td>Frontend composable pentru terminate CS</td></tr>
              <tr><td><code>ClientServicesList.vue</code></td><td>UI: buton Terminate, dialog confirmare, refresh</td></tr>
              <tr><td><code>service_mvno_operations</code> (Settings)</td><td>Configurarea metodei MVNO per serviciu + operație</td></tr>
            </table>

            <h3>Configurare necesară (checklist)</h3>
            <ol>
              <li>Serviciul (ex: imsi_subscription) trebuie să existe în <strong>Settings → Services</strong></li>
              <li>În <strong>Settings → Services → Service MVNO Operations</strong>, adaugă:
                <ul>
                  <li>Service: serviciul dorit</li>
                  <li>Operation Type: <code>terminate_subscription</code></li>
                  <li>MVNO API Method: <code>terminate_billing_subscription</code></li>
                </ul>
              </li>
              <li>În <strong>Settings → Settings Parameters</strong>, verifică:
                <ul>
                  <li><code>mvno_api_terminate_billing_endpoint</code> — URL-ul endpoint-ului MVNO pentru terminare billing</li>
                </ul>
              </li>
              <li>SIM-ul trebuie să fie <code>active</code> și să aibă <code>subscriber_id</code> setat</li>
              <li>CS-ul trebuie să fie <code>active</code> sau <code>expired</code></li>
            </ol>

            <h3>Troubleshooting</h3>
            <ul>
              <li><strong>MVNO nu primește cererea de terminate:</strong> Verifică ServiceMvnoOperation — trebuie să existe o operație <code>terminate_subscription</code> pentru serviciul respectiv</li>
              <li><strong>SIM-ul se dezactivează din rețea:</strong> Verifică că metoda MVNO setată este <code>terminate_billing_subscription</code>, NU <code>terminate_subscriber</code></li>
              <li><strong>"Only active or expired services can be terminated":</strong> CS-ul este deja terminated/suspended/quarantine</li>
              <li><strong>"Not authorized":</strong> User-ul nu aparține clientului proprietar sau unui client părinte</li>
              <li><strong>Cascade termination nedorită:</strong> Dacă serviciul este de tip <code>msisdn</code> sau <code>imsi_msisdn</code>, va face cascade (termină toate CS pe SIM). Folosește <code>imsi_subscription</code> pentru terminare individuală</li>
              <li><strong>MVNO API timeout:</strong> Verifică <code>Commands</code> — cererea a fost trimisă? Verifică status-ul comenzii</li>
            </ul>
          `
        },

        // ============================================================
        // 11. Ciclul de viață al serviciilor - Expirare & Carantină
        // ============================================================
        {
          id: 11,
          title: 'Ciclul de viață al serviciilor SIM - Activ → Expirat → Carantină → Terminat',
          icon: 'mdi-refresh-circle',
          iconColor: 'deep-orange',
          category: 'Operațiuni',
          summary: 'Stările prin care trece un serviciu (imsi_subscription) pe un SIM: activ, expirat, carantină, terminat. Procesare automată cu RenewalProcessorService.',
          content: `
            <h3>Prezentare generală</h3>
            <p>Fiecare serviciu alocat unui client (<strong>ClientService</strong>) pe un SIM urmează un ciclu de viață bine definit.
            Procesarea automată se face prin <code>RenewalProcessorService</code>, rulat periodic via <code>rake renewal:process</code>
            sau scriptul <code>analiza_expirare_simuri.sh</code>.</p>

            <h3>Diagrama stărilor</h3>
            <pre class="diagram">
┌──────────────┐    expiration_date     ┌──────────────┐
│              │    a trecut            │              │
│    ACTIVE    │ ─────────────────────► │   EXPIRED    │
│              │                        │              │
└──────┬───────┘                        └──────┬───────┘
       │                                       │
       │  autorenew=true                       │  autorenew=false
       │  + postpaid/token                     │  sau fără token
       │                                       │
       ▼                                       ▼
┌──────────────┐                        ┌──────────────┐
│  AUTO-RENEW  │                        │  QUARANTINE  │
│  (CS nou     │                        │  (perioadă   │
│   creat)     │                        │   de grație) │
└──────────────┘                        └──────┬───────┘
                                               │
                                               │  quarantine_expires_at
                                               │  a trecut
                                               ▼
                                        ┌──────────────┐
                                        │  TERMINATED  │
                                        │  (SIM terminat│
                                        │   MVNO depr.) │
                                        └──────────────┘
            </pre>

            <h3>Starea 1: ACTIVE (Activ)</h3>
            <table class="model-table">
              <tr><th>Entitate</th><th>Status</th><th>Descriere</th></tr>
              <tr><td>ClientService</td><td><code>active</code></td><td>Serviciul funcționează normal, facturarea e activă</td></tr>
              <tr><td>SIM</td><td><code>active</code></td><td>Subscriber activ în rețeaua mobilă</td></tr>
              <tr><td>MSISDN</td><td><code>allocated</code></td><td>Numărul de telefon alocat clientului</td></tr>
              <tr><td>IMSI</td><td><code>allocated</code></td><td>Cartela fizică în uz</td></tr>
            </table>
            <p><strong>Condiție de trecere:</strong> <code>expiration_date</code> &le; data curentă → trece în EXPIRED</p>

            <h3>Starea 2: EXPIRED (Expirat)</h3>
            <table class="model-table">
              <tr><th>Entitate</th><th>Status</th><th>Descriere</th></tr>
              <tr><td>ClientService</td><td><code>expired</code></td><td>Serviciul a expirat, așteaptă procesare</td></tr>
              <tr><td>SIM</td><td><code>active</code> (încă)</td><td>SIM-ul rămâne activ până la procesare</td></tr>
              <tr><td>MSISDN</td><td><code>allocated</code></td><td>Încă alocat</td></tr>
              <tr><td>IMSI</td><td><code>allocated</code></td><td>Încă alocat</td></tr>
            </table>
            <p><strong>Ce se întâmplă:</strong> La rularea <code>rake renewal:process</code>, serviciul expirat este procesat:</p>
            <ul>
              <li>Dacă <code>autorenew=true</code> + client postpaid → <strong>auto-renewal</strong> (CS nou creat automat)</li>
              <li>Dacă <code>autorenew=true</code> + token de plată salvat → <strong>auto-renewal cu plată</strong></li>
              <li>Altfel → <strong>QUARANTINE</strong></li>
            </ul>

            <h3>Starea 3: QUARANTINE (Carantină)</h3>
            <table class="model-table">
              <tr><th>Entitate</th><th>Status</th><th>Descriere</th></tr>
              <tr><td>ClientService</td><td><code>quarantine</code></td><td>Perioadă de grație, clientul poate reînnoi</td></tr>
              <tr><td>SIM</td><td><code>quarantine</code></td><td>SIM în carantină</td></tr>
              <tr><td>MSISDN</td><td><code>quarantine</code></td><td>Numărul rezervat, nu se realocă</td></tr>
              <tr><td>IMSI</td><td><code>allocated</code></td><td>Rămâne alocat</td></tr>
            </table>
            <p><strong>Durata carantinei</strong> se determină în ordinea priorității:</p>
            <ol>
              <li><strong>ClientServiceQuarantineDuration</strong> — override per CS (Settings → Service Quarantine → CS override)</li>
              <li><strong>ServiceQuarantineDuration</strong> — default per serviciu (Settings → Service Quarantine Durations)</li>
              <li><strong>30 zile</strong> — fallback implicit</li>
            </ol>
            <p><strong>Condiție de trecere:</strong> <code>quarantine_expires_at</code> &le; data/ora curentă → trece în TERMINATED</p>

            <h3>Tipuri de carantină</h3>
            <table class="model-table">
              <tr><th>Tip</th><th>Service Types</th><th>Ce se întâmplă</th></tr>
              <tr><td><strong>CASCADE</strong></td><td>msisdn, imsi_msisdn, imsi</td><td>TOATE CS-urile de pe același SIM intră în carantină. SIM + MSISDN → quarantine. Doar CS-ul părinte are <code>quarantine_expires_at</code>.</td></tr>
              <tr><td><strong>INDIVIDUAL (cu SIM)</strong></td><td>imsi_subscription</td><td>Doar CS-ul respectiv intră în carantină. SIM + MSISDN → quarantine.</td></tr>
              <tr><td><strong>INDIVIDUAL (fără SIM)</strong></td><td>client</td><td>Doar CS-ul intră în carantină, fără modificări pe SIM/MSISDN.</td></tr>
              <tr><td><strong>SKIP</strong></td><td>credit, valability</td><td>Nu intră în carantină deloc.</td></tr>
            </table>

            <h3>Starea 4: TERMINATED (Terminat)</h3>
            <table class="model-table">
              <tr><th>Entitate</th><th>Status</th><th>Descriere</th></tr>
              <tr><td>ClientService</td><td><code>terminated</code></td><td>Serviciul terminat definitiv, terminated_at setat</td></tr>
              <tr><td>SIM</td><td><code>terminated</code></td><td>Subscriber dezactivat din rețea</td></tr>
              <tr><td>MSISDN</td><td><code>available</code></td><td>Numărul returnat în pool, disponibil pentru realocare</td></tr>
              <tr><td>IMSI</td><td><code>allocated</code> (depinde)</td><td>Poate fi eliberat la deprovizionare completă</td></tr>
            </table>
            <p><strong>Ce se întâmplă la terminare:</strong></p>
            <ul>
              <li>Se apelează <code>terminate_subscriber</code> la MVNO (deprovizionare completă)</li>
              <li>SIM → terminated</li>
              <li>MSISDN → available (returnat la distribuitor dacă e cazul)</li>
              <li>Se trimit notificări (email + in-app) către utilizatorii clientului</li>
              <li>La CASCADE: se termină TOATE CS-urile de pe SIM</li>
            </ul>

            <h3>Procesarea automată</h3>
            <p>Procesarea se face prin două mecanisme:</p>
            <table class="model-table">
              <tr><th>Metodă</th><th>Comandă</th><th>Descriere</th></tr>
              <tr><td>Script shell</td><td><code>./analiza_expirare_simuri.sh</code></td><td>Script cu logging, lock file, rotație log. Suportă --dry-run și --verbose.</td></tr>
              <tr><td>Rake task</td><td><code>rake renewal:process</code></td><td>Task Rails direct. Programat în schedule.rb la 5:00 AM zilnic.</td></tr>
              <tr><td>Rake dry run</td><td><code>rake renewal:dry_run</code></td><td>Arată ce ar fi procesat fără a executa.</td></tr>
            </table>

            <h3>Flux complet pas cu pas</h3>
            <pre class="diagram">
┌─────────────────────────────────────────────────────────────────────┐
│  PROCESARE EXPIRARE SIMURI (rake renewal:process)                   │
├─────────────────────────────────────────────────────────────────────┤
│                                                                     │
│  FAZA 1: Procesare CS expirate                                      │
│  ─────────────────────────────                                      │
│  1. Selectează toate CS cu status active/expired                    │
│     unde expiration_date <= azi                                     │
│                                                                     │
│  2. Pentru fiecare CS expirat:                                      │
│     ├── autorenew=true + postpaid?                                  │
│     │   └── Creează CS NOU cu aceeași configurare                   │
│     │       ├── CS vechi → terminated (auto_renewed)                │
│     │       ├── CS nou → active cu expiration_date nouă             │
│     │       └── Trimite notificare "auto_renewed"                   │
│     │                                                               │
│     ├── autorenew=true + token plată?                               │
│     │   └── Creează CS NOU + procesează plata                       │
│     │       └── DummyPaymentService (va fi TokenPaymentService)     │
│     │                                                               │
│     └── altfel → QUARANTINE                                         │
│         ├── Calculează durata: CS override > Service > 30 zile      │
│         ├── CS → quarantine + quarantine_expires_at setat            │
│         ├── SIM → quarantine (dacă e cazul)                         │
│         ├── MSISDN → quarantine (dacă e cazul)                      │
│         └── Trimite notificare "quarantine_started"                 │
│                                                                     │
│  FAZA 2: Procesare carantină expirate                               │
│  ────────────────────────────────────                               │
│  1. Selectează CS cu status quarantine                              │
│     unde quarantine_expires_at <= acum                              │
│                                                                     │
│  2. Pentru fiecare CS:                                              │
│     ├── Apel MVNO terminate_subscriber (deprovizionare)             │
│     ├── CS → terminated (quarantine_expired)                        │
│     ├── SIM → terminated                                            │
│     ├── MSISDN → available (returnat distribuitor)                  │
│     ├── CASCADE: termină toate CS de pe SIM                         │
│     └── Trimite notificare "quarantine_terminated"                  │
└─────────────────────────────────────────────────────────────────────┘
            </pre>

            <h3>Programare automată (cron)</h3>
            <p>Scriptul poate fi programat în crontab sau prin Whenever gem:</p>
            <pre class="diagram">
# Whenever gem (config/schedule.rb) - deja configurat:
every 1.day, at: '5:00 am' do
  rake 'renewal:process'
end

# Sau crontab direct:
# Zilnic la 5:00 AM
0 5 * * * /path/to/my_veridian_api/analiza_expirare_simuri.sh

# La fiecare 6 ore
0 */6 * * * /path/to/my_veridian_api/analiza_expirare_simuri.sh

# Dry run zilnic la 4:00 AM (doar raport)
0 4 * * * /path/to/my_veridian_api/analiza_expirare_simuri.sh --dry-run
            </pre>

            <h3>Ordinea procesării zilnice</h3>
            <table class="model-table">
              <tr><th>Ora</th><th>Task</th><th>Ce face</th></tr>
              <tr><td>01:00</td><td><code>billing:generate_daily_reports</code></td><td>Rapoarte de facturare zilnice</td></tr>
              <tr><td>05:00</td><td><code>renewal:process</code></td><td>Expirare → carantină → terminare SIM-uri</td></tr>
              <tr><td>06:00</td><td><code>dunning:process</code></td><td>Reguli de colectare (notificări, suspendări)</td></tr>
            </table>
            <p><strong>Notă:</strong> Renewal rulează ÎNAINTE de dunning pentru ca serviciile expirate să fie procesate (quarantine/terminate) înainte de a aplica regulile de dunning pe ele.</p>

            <h3>Notificări trimise</h3>
            <table class="model-table">
              <tr><th>Eveniment</th><th>Template</th><th>Variabile disponibile</th></tr>
              <tr><td>Intrare în carantină</td><td><code>quarantine_started</code></td><td>client_name, service_name, msisdn_number, quarantine_days, quarantine_expires_at</td></tr>
              <tr><td>Carantină cascade (copii)</td><td><code>quarantine_cascade_started</code></td><td>Aceleași + info despre CS-ul părinte</td></tr>
              <tr><td>Terminare după carantină</td><td><code>quarantine_terminated</code></td><td>client_name, service_name, msisdn_number, termination_reason</td></tr>
              <tr><td>Terminare cascade</td><td><code>quarantine_cascade_terminated</code></td><td>Aceleași</td></tr>
              <tr><td>Auto-reînnoire</td><td><code>auto_renewed</code></td><td>client_name, service_name, expiration_date</td></tr>
            </table>
            <p>Template-urile se configurează în <strong>Settings → Notification Templates</strong>.</p>

            <h3>Exemplu concret: imsi_subscription pe SIM</h3>
            <pre class="diagram">
Ziua 0: Clientul activează imsi_subscription (plan voce+SMS+date)
  → CS #100: status=active, expiration_date=2026-03-17
  → SIM #50: status=active, subscriber_id=SUB_001
  → MSISDN #200: status=allocated (40783800121)

Ziua 30 (17 martie): expiration_date a trecut
  → rake renewal:process rulează
  → CS #100: autorenew=false → QUARANTINE
  → CS #100: status=quarantine, quarantine_expires_at=2026-04-16
  → SIM #50: status=quarantine
  → MSISDN #200: status=quarantine
  → Email trimis: "Serviciul dvs. a intrat în carantină, 30 zile"

Ziua 60 (16 aprilie): quarantine_expires_at a trecut
  → rake renewal:process rulează
  → MVNO: terminate_subscriber(SUB_001) → subscriber dezactivat
  → CS #100: status=terminated, terminated_at=2026-04-16
  → SIM #50: status=terminated
  → MSISDN #200: status=available (returnat în pool)
  → Email trimis: "Serviciul dvs. a fost terminat"
            </pre>

            <h3>Fișiere implicate</h3>
            <table class="model-table">
              <tr><th>Fișier</th><th>Rol</th></tr>
              <tr><td><code>app/services/renewal_processor_service.rb</code></td><td>Logica completă de procesare expirare/carantină/terminare</td></tr>
              <tr><td><code>lib/tasks/renewal.rake</code></td><td>Rake task: renewal:process și renewal:dry_run</td></tr>
              <tr><td><code>analiza_expirare_simuri.sh</code></td><td>Script shell cu logging, lock file, programabil cron</td></tr>
              <tr><td><code>config/schedule.rb</code></td><td>Programare Whenever gem (5:00 AM zilnic)</td></tr>
              <tr><td><code>app/services/dummy_payment_service.rb</code></td><td>Procesare plăți (placeholder, va fi TokenPaymentService)</td></tr>
              <tr><td><code>app/services/mvno_provisioning_service.rb</code></td><td>Apeluri MVNO terminate_subscriber/terminate_billing</td></tr>
              <tr><td><code>app/models/client_service.rb</code></td><td>Model CS cu quarantine_expires_at, autorenew, expiration_date</td></tr>
              <tr><td><code>app/models/service_quarantine_duration.rb</code></td><td>Durate carantină default per serviciu</td></tr>
              <tr><td><code>app/models/client_service_quarantine_duration.rb</code></td><td>Override carantină per CS individual</td></tr>
            </table>

            <h3>Troubleshooting</h3>
            <ul>
              <li><strong>CS-ul nu intră în carantină:</strong> Verifică că <code>expiration_date</code> e setat și a trecut. Verifică că statusul e <code>active</code> sau <code>expired</code>.</li>
              <li><strong>Carantina nu se termină:</strong> Verifică <code>quarantine_expires_at</code> — doar CS-ul "părinte" (cu quarantine_expires_at non-null) declanșează terminarea.</li>
              <li><strong>MVNO nu primește terminate:</strong> Verifică că SIM-ul are <code>subscriber_id</code> și IMSI + MSISDN setate.</li>
              <li><strong>Durata carantinei greșită:</strong> Verifică prioritatea: CS override > Service default > 30 zile.</li>
              <li><strong>Auto-renewal nu funcționează:</strong> Verifică <code>autorenew=true</code> pe CS + clientul e postpaid sau are token salvat.</li>
              <li><strong>Scriptul shell nu rulează:</strong> Verifică: <code>chmod +x analiza_expirare_simuri.sh</code>, lock file stale în <code>/tmp/renewal_processor.lock</code>.</li>
              <li><strong>Dry run:</strong> Folosește <code>rake renewal:dry_run</code> sau <code>./analiza_expirare_simuri.sh --dry-run</code> pentru a vedea ce ar fi procesat fără a executa.</li>
            </ul>
          `
        },

        // ============================================================
        // 12. Alerte Trafic & Profile Limitări
        // ============================================================
        {
          id: 12,
          title: 'Alerte Trafic & Profile Limitări - Monitorizare consum și acțiuni automate',
          icon: 'mdi-bell-alert',
          iconColor: 'red',
          category: 'Operațiuni',
          summary: 'Sistem de alerte pe consum de trafic (date, SMS, voce, cost) cu notificări automate, suspendare SIM, profile reutilizabile și programare periodică.',
          content: `
            <h3>Prezentare generală</h3>
            <p>Sistemul de alerte trafic monitorizează consumul SIM-urilor și declanșează acțiuni automate când pragurile configurate sunt depășite.
            Se accesează din meniul <strong>Alerte Trafic</strong> sau ruta <code>/traffic-alerts</code>.</p>
            <p>Pagina are <strong>3 tab-uri</strong>: Reguli Alerte, Profile Limitări, Jurnal Alerte.</p>

            <h3>Concepte cheie</h3>
            <table class="model-table">
              <tr><th>Concept</th><th>Descriere</th></tr>
              <tr><td><strong>Regulă alertă</strong></td><td>Definește CE se monitorizează (metrica, prag, perioadă) și CE se face când pragul e depășit (notificare, suspendare)</td></tr>
              <tr><td><strong>Profil limitări</strong></td><td>Un SET reutilizabil de reguli. Se creează o dată și se asignează la mai multe SIM-uri/grupuri</td></tr>
              <tr><td><strong>Asignare profil</strong></td><td>Legătura dintre un profil și un grup IMSI sau SIM individual</td></tr>
              <tr><td><strong>Regulă standalone</strong></td><td>O regulă care NU aparține unui profil - funcționează independent</td></tr>
            </table>

            <h3>Reguli de alertă - parametri</h3>
            <table class="model-table">
              <tr><th>Parametru</th><th>Opțiuni</th><th>Descriere</th></tr>
              <tr><td><strong>Metrica</strong></td><td>Date (MB), SMS, Voce (min), Cost (EUR)</td><td>Ce se măsoară</td></tr>
              <tr><td><strong>Prag</strong></td><td>Valoare numerică &gt; 0</td><td>Valoarea la care se declanșează alerta (ex: 100 MB)</td></tr>
              <tr><td><strong>Tip rețea</strong></td><td>National, Roaming, Toate</td><td>Pe ce tip de trafic se aplică</td></tr>
              <tr><td><strong>Perioadă</strong></td><td>Zilnic, Lunar</td><td>Fereastra de timp pentru calcul consum</td></tr>
              <tr><td><strong>Acțiune</strong></td><td>Email, In-App, Email+In-App, Suspendare SIM</td><td>Ce se face când pragul e depășit</td></tr>
              <tr><td><strong>Cooldown</strong></td><td>Ore (ex: 24)</td><td>Nu re-alertează pentru același SIM în X ore</td></tr>
              <tr><td><strong>Prioritate</strong></td><td>Număr (0 = cea mai mare)</td><td>Ordinea procesării regulilor</td></tr>
            </table>

            <h3>Scope (Aplicare) - pe cine se aplică regula</h3>
            <table class="model-table">
              <tr><th>Scope Type</th><th>Descriere</th></tr>
              <tr><td><strong>Doar clientul</strong></td><td>Verifică doar SIM-urile clientului selectat (managed_by_client)</td></tr>
              <tr><td><strong>Client + subclienti</strong></td><td>Verifică SIM-urile clientului + toți subclienii din ierarhie</td></tr>
              <tr><td><strong>Toate SIM-urile</strong></td><td>Verifică toate SIM-urile din sistem (doar admin)</td></tr>
            </table>

            <h3>Targetare specifică (opțional)</h3>
            <p>Pe lângă scope, o regulă poate fi restricționată la:</p>
            <ul>
              <li><strong>SIM specific</strong> — alerta se aplică doar pe un singur SIM (selectat din lista clientului)</li>
              <li><strong>Grup IMSI</strong> — alerta se aplică pe toate SIM-urile din grupul respectiv</li>
              <li><strong>Toate (din scope)</strong> — fără restricție suplimentară, se aplică pe tot scope-ul</li>
            </ul>
            <p><strong>Prioritate target:</strong> SIM specific &gt; Grup IMSI &gt; Scope (client/subclienti/toate)</p>

            <h3>Diagrama fluxului de procesare</h3>
            <pre class="diagram">
┌─────────────────────────────────────────────────────────────┐
│  PROCESARE ALERTE TRAFIC (TrafficAlertProcessorService)     │
├─────────────────────────────────────────────────────────────┤
│                                                             │
│  FAZA 1: Reguli standalone (fără profil)                    │
│  ────────────────────────────────────────                   │
│  1. Selectează regulile enabled + limitation_profile_id=nil │
│  2. Pentru fiecare regulă:                                  │
│     ├── Rezolvă SIM-uri: sim_id > imsi_group > scope_type  │
│     └── Pentru fiecare SIM:                                 │
│         ├── Verifică cooldown (nu re-alerteaza)             │
│         ├── Citește TrafficSummary (consum curent)          │
│         ├── Compară cu threshold                            │
│         └── Dacă depășit → execută acțiune                  │
│                                                             │
│  FAZA 2: Profile de limitări                                │
│  ────────────────────────────                               │
│  1. Selectează profilele enabled                            │
│  2. Pentru fiecare profil:                                  │
│     ├── Încarcă regulile enabled din profil                 │
│     ├── Rezolvă SIM-uri din asignări (grupuri + SIM-uri)   │
│     └── Pentru fiecare regulă:                              │
│         ├── Dacă regula are sim_id/group → folosește ale ei │
│         ├── Altfel → folosește SIM-urile din asignări       │
│         └── Procesează la fel ca standalone                  │
│                                                             │
└─────────────────────────────────────────────────────────────┘
            </pre>

            <h3>Profile Limitări - cum funcționează</h3>
            <p>Un profil grupează mai multe reguli și le aplică pe un set de SIM-uri/grupuri:</p>
            <pre class="diagram">
Exemplu: Profil "Limită Roaming Angajați"
├── Regulă 1: Date roaming > 100 MB → Notificare email
├── Regulă 2: Date roaming > 500 MB → Notificare email + in-app
├── Regulă 3: Cost roaming > 50 EUR → Suspendare SIM
│
Asignări:
├── Grup IMSI "Departament Vânzări" (15 SIM-uri)
├── Grup IMSI "Departament Tehnic" (8 SIM-uri)
└── SIM #1376 (director general - monitorizare specială)
            </pre>
            <p><strong>Avantaj:</strong> Modifici o regulă din profil → se aplică automat la toate SIM-urile asignate. Nu trebuie să modifici 23 de reguli individual.</p>

            <h3>Standalone vs. Profil - când folosești ce</h3>
            <table class="model-table">
              <tr><th>Situație</th><th>Recomandare</th></tr>
              <tr><td>O singură alertă pe un SIM specific</td><td><strong>Standalone</strong> - regulă simplă cu sim_id setat</td></tr>
              <tr><td>Aceleași limite pe mai multe SIM-uri/grupuri</td><td><strong>Profil</strong> - creezi profilul, asignezi la grupuri</td></tr>
              <tr><td>Alertă globală (toate SIM-urile)</td><td><strong>Standalone</strong> cu scope "Toate SIM-urile"</td></tr>
              <tr><td>Limite diferite per departament</td><td><strong>Profile multiple</strong> - un profil per departament, asignat la grupul IMSI</td></tr>
              <tr><td>SIM cu limite speciale (VIP)</td><td><strong>Regulă în profil cu sim_id</strong> - regula specifică override-uiește asignările</td></tr>
            </table>

            <h3>Acțiuni disponibile</h3>
            <table class="model-table">
              <tr><th>Acțiune</th><th>Ce face</th><th>Când o folosești</th></tr>
              <tr><td><strong>Notificare Email</strong></td><td>Trimite email la toți utilizatorii activi ai clientului</td><td>Alertă informativă, prag intermediar</td></tr>
              <tr><td><strong>Notificare In-App</strong></td><td>Creează notificare în platformă (clopoțel)</td><td>Alertă internă, vizibilă la login</td></tr>
              <tr><td><strong>Email + In-App</strong></td><td>Ambele simultan</td><td>Alertă importantă</td></tr>
              <tr><td><strong>Suspendare SIM</strong></td><td>SIM → suspended, toate CS active → suspended + notificare in-app</td><td>Depășire critică, protecție costuri</td></tr>
            </table>

            <h3>Notificări personalizate</h3>
            <p>La crearea unei reguli, poți personaliza subiectul și corpul notificării cu variabile:</p>
            <table class="model-table">
              <tr><th>Variabilă</th><th>Descriere</th><th>Exemplu</th></tr>
              <tr><td><code>{client_name}</code></td><td>Numele clientului</td><td>SC Exemplu SRL</td></tr>
              <tr><td><code>{sim_msisdn}</code></td><td>Numărul de telefon (MSISDN)</td><td>40764111211</td></tr>
              <tr><td><code>{sim_id}</code></td><td>ID-ul SIM-ului</td><td>1376</td></tr>
              <tr><td><code>{metric_label}</code></td><td>Numele metricii</td><td>Date mobile</td></tr>
              <tr><td><code>{current_value}</code></td><td>Consumul curent</td><td>63.13</td></tr>
              <tr><td><code>{threshold_value}</code></td><td>Pragul configurat</td><td>50.00</td></tr>
              <tr><td><code>{unit}</code></td><td>Unitatea de măsură</td><td>MB, EUR, min, msg</td></tr>
              <tr><td><code>{network_type}</code></td><td>Tipul rețelei</td><td>roaming</td></tr>
              <tr><td><code>{period_type}</code></td><td>Perioada</td><td>daily, monthly</td></tr>
            </table>
            <p><strong>Exemplu:</strong> Subiect: <code>Alerta: {metric_label} depasit {threshold_value} {unit} pe {sim_msisdn}</code></p>

            <h3>Sursa datelor: TrafficSummary</h3>
            <p><strong>Important:</strong> Alertele verifică tabelul <code>traffic_summaries</code>, NU <code>traffic_records</code> direct.
            Summary-urile sunt agregate zilnice/lunare per SIM, create de <code>TrafficImportService</code> la importul CDR-urilor.</p>
            <p>Dacă o alertă nu se declanșează deși există un TrafficRecord, verifică:</p>
            <ul>
              <li>Există un <strong>TrafficSummary</strong> pentru acel SIM + perioadă + network_type?</li>
              <li>Datele din summary sunt <strong>actualizate</strong>? (rulează import CDR mai întâi)</li>
              <li>Regula verifică <strong>perioada corectă</strong>? (daily = data curentă, monthly = prima zi din lună)</li>
              <li>Regula verifică <strong>tipul rețelei corect</strong>? (national vs roaming vs all)</li>
            </ul>

            <h3>Cum rulezi alertele</h3>
            <table class="model-table">
              <tr><th>Metodă</th><th>Comandă / Acțiune</th><th>Descriere</th></tr>
              <tr><td><strong>Din UI</strong></td><td>Buton verde <em>Ruleaza Alerte</em></td><td>Rulare sincronă, doar admin. Afișează rezultatul.</td></tr>
              <tr><td><strong>Din UI (test)</strong></td><td>Buton <em>Test Dry Run</em></td><td>Arată ce s-ar declanșa fără a executa efectiv</td></tr>
              <tr><td><strong>Rake task</strong></td><td><code>rake traffic_alerts:process</code></td><td>Rulare din terminal, executare efectivă</td></tr>
              <tr><td><strong>Rake dry run</strong></td><td><code>rake traffic_alerts:dry_run</code></td><td>Doar afișare, fără execuție</td></tr>
              <tr><td><strong>Cron (automat)</strong></td><td>Configurat în <code>schedule.rb</code></td><td>La fiecare 4 ore automat</td></tr>
              <tr><td><strong>Background job</strong></td><td>API cu <code>mode=async</code></td><td>Procesare în background (TrafficAlertJob)</td></tr>
            </table>

            <h3>Exemplu pas cu pas: Crearea unei alerte pe SIM specific</h3>
            <pre class="diagram">
1. Accesează /traffic-alerts
2. Tab "Reguli Alerte" → click butonul "+ Regula"
3. Completează:
   - Nume: "Alertă date roaming SIM 40764111211"
   - Metrica: Date (MB)
   - Prag: 50 (se alertează la 50 MB)
   - Tip rețea: Roaming
   - Perioadă: Zilnic
   - Acțiune: Notificare Email
   - Cooldown: 24 ore
4. Secțiunea "Ținta alertei":
   - Client: selectează clientul din ierarhie
   - Aplicare: Doar clientul
   - Filtrează pe: SIM specific
   - SIM: selectează SIM-ul din autocomplete
5. Click "Creează"
6. Testează cu "Test Dry Run"
            </pre>

            <h3>Exemplu: Profil cu asignare pe grup</h3>
            <pre class="diagram">
1. Tab "Profile Limitări" → click "+ Profil Nou"
2. Completează: Nume: "Standard Roaming", Client: distribuitorul
3. Click profilul creat → se deschide panoul detalii
4. Click "+ Regulă" → adaugă 3 reguli:
   - Date roaming > 100 MB → email
   - Date roaming > 500 MB → email + in-app
   - Cost > 50 EUR → suspendare
5. Click "+ Asignează" → selectează client → selectează grup IMSI
6. Toate SIM-urile din grup primesc cele 3 reguli automat
            </pre>

            <h3>Programare automată (cron)</h3>
            <pre class="diagram">
# config/schedule.rb - deja configurat:
# Procesare alerte la fiecare 4 ore
every '0 */4 * * *' do
  rake 'traffic_alerts:process'
end

# Ordinea procesării zilnice:
# 01:00 - billing:generate_daily_reports
# 05:00 - renewal:process (expirare/carantină)
# 06:00 - dunning:process (reguli colectare)
# */4h  - traffic_alerts:process (alerte consum)
            </pre>

            <h3>Fișiere implicate</h3>
            <table class="model-table">
              <tr><th>Fișier</th><th>Rol</th></tr>
              <tr><td><code>app/services/traffic_alert_processor_service.rb</code></td><td>Logica completă de procesare alerte (standalone + profile)</td></tr>
              <tr><td><code>app/controllers/api/v1/traffic_alerts_controller.rb</code></td><td>API endpoints (CRUD reguli, profile, asignări, logs, process, dry run)</td></tr>
              <tr><td><code>app/models/traffic_alert_rule.rb</code></td><td>Model regulă: metrică, prag, acțiune, scope, target</td></tr>
              <tr><td><code>app/models/limitation_profile.rb</code></td><td>Model profil: set reutilizabil de reguli</td></tr>
              <tr><td><code>app/models/limitation_profile_assignment.rb</code></td><td>Legătura profil ↔ grup IMSI / SIM</td></tr>
              <tr><td><code>app/models/traffic_alert_log.rb</code></td><td>Jurnal execuții: ce s-a declanșat, când, pe cine</td></tr>
              <tr><td><code>app/jobs/traffic_alert_job.rb</code></td><td>Background job pentru procesare async</td></tr>
              <tr><td><code>lib/tasks/traffic_alerts.rake</code></td><td>Rake tasks: process și dry_run</td></tr>
              <tr><td><code>config/schedule.rb</code></td><td>Programare cron (la fiecare 4 ore)</td></tr>
              <tr><td><code>src/views/TrafficAlertRulesView.vue</code></td><td>Frontend: pagina principală cu 3 tab-uri</td></tr>
              <tr><td><code>src/components/dialogs/TrafficAlertRuleDialog.vue</code></td><td>Dialog creare/editare regulă</td></tr>
              <tr><td><code>src/components/dialogs/LimitationProfileDialog.vue</code></td><td>Dialog creare/editare profil</td></tr>
              <tr><td><code>src/components/dialogs/ProfileAssignmentDialog.vue</code></td><td>Dialog asignare profil la SIM/grup</td></tr>
              <tr><td><code>src/composables/useTrafficAlertsAPI.js</code></td><td>Funcții API frontend</td></tr>
            </table>

            <h3>Troubleshooting</h3>
            <ul>
              <li><strong>Alerta nu se declanșează:</strong> Verifică că există <code>TrafficSummary</code> pentru SIM + data curentă + network_type. Procesorul folosește summary-uri, nu records direct.</li>
              <li><strong>Regula e în profil dar nu funcționează:</strong> Dacă regula are <code>sim_id</code> sau <code>imsi_group_id</code> setat, le folosește pe acelea. Dacă nu, verifică asignările profilului - trebuie să existe cel puțin o asignare.</li>
              <li><strong>Cooldown blocat:</strong> Verifică în tab-ul Jurnal dacă există o execuție recentă. Regula nu se re-declanșează în intervalul de cooldown.</li>
              <li><strong>Suspendarea nu funcționează:</strong> Verifică că există statusul <code>suspended</code> în <code>sim_statuses</code> și <code>client_service_statuses</code>.</li>
              <li><strong>Email-ul nu ajunge:</strong> Verifică configurarea mailer-ului Rails și că utilizatorii clientului au email setat și sunt activi.</li>
              <li><strong>Dry run arată rezultate dar execuția nu:</strong> Verifică cooldown-ul - dry run nu verifică cooldown, dar execuția reală da.</li>
            </ul>
          `
        },
        {
          id: 13,
          title: 'Roaming - Activare/Dezactivare zone de roaming pe SIM',
          icon: 'mdi-radio-tower',
          iconColor: 'deep-orange',
          category: 'Operațiuni SIM',
          summary: 'Cum funcționează sistemul de roaming cu 2 zone, activare/dezactivare, și comunicarea cu MVNO API.',
          content: `
            <h3>Concepte cheie</h3>
            <ul>
              <li><strong>Roaming Zone:</strong> Există 2 zone de roaming disponibile: <strong>Zona 1</strong> (mapată la ProductCode <code>R2</code>) și <strong>Zona 2</strong> (mapată la ProductCode <code>R5</code>).</li>
              <li><strong>roaming_type_id:</strong> Câmp pe tabelul <code>sims</code> care indică starea curentă a roamingului:
                <ul>
                  <li><code>nil</code> sau <code>22</code> = roaming <strong>inactiv</strong></li>
                  <li>Alt ID de ProductCode = roaming <strong>activ</strong> cu zona respectivă</li>
                </ul>
              </li>
              <li><strong>Lista de țări:</strong> Configurabilă din Settings → Parametri cu <code>countries_roaming_zone_1</code> și <code>countries_roaming_zone_2</code>.</li>
            </ul>

            <h3>Acces</h3>
            <ol>
              <li>Din <strong>SIM-uri</strong> → pe un SIM activ → More Actions → <strong>Servicii Rețea</strong></li>
              <li>Sau din <strong>SIM Details</strong> → butonul <strong>Servicii Rețea</strong> (vizibil dacă SIM-ul e activ și are subscriber_id)</li>
            </ol>

            <h3>Interfața</h3>
            <p>Pagina <code>Servicii Rețea SIM</code> afișează:</p>
            <ul>
              <li><strong>Servicii Principale</strong> (P-codes) - informativ, read-only</li>
              <li><strong>Roaming</strong> - 2 carduri expandabile (Zona 1, Zona 2):
                <ul>
                  <li>Click pe <em>Vezi țări</em> arată lista de țări din Settings</li>
                  <li>Buton <strong>Activează</strong> / <strong>Dezactivează</strong> în funcție de starea curentă</li>
                  <li>Zona activă are border colorat și chip <em>ACTIV</em></li>
                </ul>
              </li>
              <li><strong>Servicii Suplimentare</strong> (S-codes) - toggle individual per serviciu</li>
            </ul>

            <h3>Scenarii de utilizare</h3>
            <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
              <tr style="background: #f5f5f5;"><th>Stare curentă</th><th>Acțiune</th><th>Ce se întâmplă</th></tr>
              <tr><td>Fără roaming (nil/22)</td><td>Click Activează Zona 1</td><td>Se trimite comanda de activare → MVNO activează R2 → callback → roaming_type_id = ID(R2)</td></tr>
              <tr><td>Zona 1 activă</td><td>Click Dezactivează Zona 1</td><td>Se trimite comanda de dezactivare → MVNO dezactivează R2 → callback → roaming_type_id = 22</td></tr>
              <tr><td>Zona 1 activă</td><td>Click Activează Zona 2</td><td>MVNO-ul dezactivează R2 intern, apoi activează R5 → callback → roaming_type_id = ID(R5)</td></tr>
              <tr><td>Zona 2 activă</td><td>Click Activează Zona 1</td><td>MVNO-ul dezactivează R5 intern, apoi activează R2 → callback → roaming_type_id = ID(R2)</td></tr>
            </table>

            <h3>Flow tehnic</h3>
            <ol>
              <li>Frontend trimite <code>POST /home/change_roaming_api</code> cu <code>sim_id</code> și <code>selected_zone_id</code> (1 sau 2)</li>
              <li>Backend determină acțiunea (Add/Delete) bazat pe <code>sim.roaming_type_id</code></li>
              <li>Backend blochează SIM-ul (<code>mvno_pending = true</code>) pentru a preveni comenzi concurente</li>
              <li>Backend trimite la MVNO API: <code>subscriber_id</code>, <code>roaming_zone</code> (zona curentă), <code>selected_zone_id</code></li>
              <li>MVNO procesează comanda (dezactivare vechi + activare nou dacă e schimbare de zonă)</li>
              <li>MVNO trimite callback la <code>POST /home/callback_network_service_api</code></li>
              <li>Backend actualizează <code>roaming_type_id</code> pe SIM și deblochează (<code>mvno_pending = false</code>)</li>
              <li>Frontend auto-refresh la 5 secunde detectează schimbarea și actualizează interfața</li>
            </ol>

            <h3>Mapping zone → ProductCode</h3>
            <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse;">
              <tr style="background: #f5f5f5;"><th>Zona</th><th>ProductCode</th><th>selected_zone_id</th></tr>
              <tr><td>Zona 1</td><td>R2</td><td>1</td></tr>
              <tr><td>Zona 2</td><td>R5</td><td>2</td></tr>
            </table>

            <h3>Configurare țări</h3>
            <p>Din Settings → Parametri:</p>
            <ul>
              <li><code>countries_roaming_zone_1</code> - lista de țări pentru Zona 1</li>
              <li><code>countries_roaming_zone_2</code> - lista de țări pentru Zona 2</li>
            </ul>

            <h3>Troubleshooting</h3>
            <ul>
              <li><strong>Butoanele sunt blocate:</strong> SIM-ul este în stare <code>mvno_pending</code> - așteaptă callback de la MVNO. Verifică în Commands dacă există o comandă pending.</li>
              <li><strong>Roaming nu se activează:</strong> SIM-ul trebuie să fie activ și să aibă <code>subscriber_id</code> setat (provizionat la MVNO).</li>
              <li><strong>Nu apare butonul Servicii Rețea:</strong> Verifică că SIM-ul este activ și are subscriber_id în baza de date.</li>
              <li><strong>Callback nu vine:</strong> Verifică endpoint-ul de callback în configurarea MVNO și că ruta <code>callback_network_service_api</code> e accesibilă.</li>
            </ul>
          `
        },
        {
          id: 14,
          title: 'Dashboard Configurabil - Template System & Widget Trafic',
          icon: 'mdi-view-dashboard',
          iconColor: 'indigo',
          category: 'Dashboard',
          summary: 'Ce este dashboard-ul configurabil, cum funcționează template system, secțiunile disponibile și widget-ul de trafic.',
          content: `
            <h3>Ce este Dashboard-ul Configurabil?</h3>
            <p>Dashboard-ul este prima pagină afișată la login. Conținutul este controlat printr-un <strong>Template System</strong> - fiecare client poate avea un template diferit cu secțiuni configurabile.</p>

            <h3>Template System</h3>
            <ul>
              <li><strong>Dashboard Template Type:</strong> Definește un tip de template (ex: "Admin Dashboard", "Reseller Dashboard")</li>
              <li><strong>Secțiuni:</strong> Fiecare template are o listă ordonată de secțiuni care se afișează pe dashboard</li>
              <li><strong>Asignare:</strong> Un template poate fi setat ca default sau asignat specific unui client</li>
            </ul>

            <h3>Secțiuni Disponibile</h3>
            <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
              <tr style="background: #f5f5f5;"><th>Secțiune</th><th>Descriere</th><th>Componenta</th></tr>
              <tr><td>Navigation Cards</td><td>Carduri de navigare rapidă la pagini importante</td><td><code>NavigationCardsSection</code></td></tr>
              <tr><td>Quick Actions</td><td>Acțiuni rapide (provizionare SIM, adaugă client etc.)</td><td><code>QuickActionsSection</code></td></tr>
              <tr><td>SIM Stats</td><td>Statistici SIM-uri: active, inactive, suspendate, expirate</td><td><code>SimsStatsSection</code></td></tr>
              <tr><td>Services Stats</td><td>Statistici servicii active per tip</td><td><code>ServicesStatsSection</code></td></tr>
              <tr><td>SIM Search</td><td>Căutare rapidă SIM după MSISDN sau IMSI</td><td><code>SimSearchSection</code></td></tr>
              <tr><td>SIM Advanced Search</td><td>Căutare avansată cu filtre multiple</td><td><code>SimAdvancedSearchSection</code></td></tr>
              <tr><td>Client Search</td><td>Căutare client după nume sau ID</td><td><code>ClientSearchSection</code></td></tr>
              <tr><td>Notifications</td><td>Ultimele notificări in-app</td><td><code>NotificationsSection</code></td></tr>
              <tr><td>Hierarchy</td><td>Arborele de clienți și subclienți</td><td><code>HierarchySection</code></td></tr>
              <tr><td>Commissions</td><td>Rezumat comisioane și balanță MLM</td><td><code>CommissionsSection</code></td></tr>
              <tr><td>Traffic Stats</td><td>Widget grafice consum trafic (date, SMS, voce) - ultimele 30 zile</td><td><code>TrafficStatsSection</code></td></tr>
            </table>

            <h3>Widget Trafic (Traffic Stats)</h3>
            <p>Secțiunea <strong>Traffic Stats</strong> afișează pe dashboard:</p>
            <ul>
              <li><strong>KPI Cards:</strong> 4 carduri cu totaluri pe ultimele 30 zile (Date MB, SMS-uri, Minute voce, Cost EUR)</li>
              <li><strong>Grafic:</strong> Grafic cu evoluția zilnică a traficului pe ultimele 30 zile (folosind Chart.js)</li>
              <li><strong>Unități dinamice:</strong> Datele se afișează în MB sau GB automat în funcție de volum</li>
            </ul>

            <h3>Configurare Template</h3>
            <ol>
              <li>Du-te la <strong>Settings → Dashboard Templates</strong></li>
              <li>Creează un template nou sau editează unul existent</li>
              <li>Adaugă/elimină secțiuni și configurează ordinea lor (drag & drop sau butoane sus/jos)</li>
              <li>Setează template-ul ca <strong>Default</strong> sau asignează-l unui client specific</li>
              <li>Clientul va vedea la login dashboard-ul cu secțiunile configurate</li>
            </ol>

            <h3>Troubleshooting</h3>
            <ul>
              <li><strong>Dashboard-ul e gol:</strong> Verifică că clientul are un template asignat sau că există un template default.</li>
              <li><strong>Traffic Stats nu apare:</strong> Secțiunea trebuie adăugată explicit în template. De asemenea, trebuie să existe date de trafic importate (CDR).</li>
              <li><strong>Graficele nu se încarcă:</strong> Verifică că <code>chart.js</code> este instalat (<code>npm install chart.js</code>).</li>
            </ul>
          `
        }
      ]
    }
  },
  computed: {
    categories() {
      return [...new Set(this.guides.map(g => g.category))];
    },
    filteredGuides() {
      return this.guides.filter(guide => {
        const matchesSearch = !this.searchTerm ||
          guide.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          guide.summary.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          guide.content.toLowerCase().includes(this.searchTerm.toLowerCase());
        const matchesCategory = !this.selectedCategory || guide.category === this.selectedCategory;
        return matchesSearch && matchesCategory;
      });
    }
  },
  methods: {
    toggleGuide(id) {
      const idx = this.expandedGuides.indexOf(id);
      if (idx >= 0) {
        this.expandedGuides.splice(idx, 1);
      } else {
        this.expandedGuides.push(id);
      }
    }
  }
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.howto-content h3 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 16px;
  margin-bottom: 8px;
  color: #1976d2;
}
.howto-content h3:first-child {
  margin-top: 0;
}
.howto-content ul, .howto-content ol {
  margin-bottom: 8px;
  padding-left: 24px;
}
.howto-content li {
  margin-bottom: 4px;
}
.howto-content code {
  background: #e3f2fd;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
  color: #1565c0;
}
.howto-content pre.diagram {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
  line-height: 1.4;
  white-space: pre;
}
.howto-content p {
  margin-bottom: 8px;
}
.howto-content .model-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 12px;
}
.howto-content .model-table th,
.howto-content .model-table td {
  border: 1px solid #e0e0e0;
  padding: 6px 10px;
  text-align: left;
  font-size: 0.9em;
}
.howto-content .model-table th {
  background: #f5f5f5;
  font-weight: 600;
}
.howto-content .model-table tr:hover td {
  background: #fafafa;
}
</style>
